import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';

// SVG Icons (unchanged)
const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const PlusIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

const TrashIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);

const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);

const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

const SaveIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"></path>
    <polyline points="17 21 17 13 7 13 7 21"></polyline>
    <polyline points="7 3 7 8 15 8"></polyline>
  </svg>
);

const AutofillIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const API_URL = '/api';

// Status options (unchanged)
const STATUS_OPTIONS = [
  { value: '1', label: 'Active' },
  { value: '0', label: 'Inactive' },
];

// Inventory flag options (unchanged)
const INVENTORY_FLAG_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const initialHeader = {
  stockOutId: '',
  stockOutDate: new Date().toISOString().split('T')[0],
  stockOutByUserId: '',
  stockOutUserName: '',
  note: '',
  status: '1',
  inventoryFlag: 'yes',
  totalQuantity: '0',
  branchCode: '',
  department_id: '',
};

const initialItem = {
  itemCode: '',
  itemName: '',
  uom: '',
  quantityMinus: '',
  rate: '',
  value: '0',
  location: '',
  reason: '',
  department_id: '',
  isManuallyAdded: false,
  productSelected: null,
  isQuantityModified: false,
  originalQuantity: '',
  batchNumber: '',
  expiryDate: '',
  showBatchPopup: false,
  availableBatches: [],
  batchError: '',
  originalBatchNumber: '',
  originalExpiryDate: '',
  isBatchModified: false,
  allowBatchOnProduct: false,
  allowProductNegativeInventory: false,
  batchQuantity: 0,
  trackingType: 'none', // 'none', 'batch', or 'serial'
  batches: [],
  serials: [],
};

// Custom Product Dropdown Component with Search
const ProductSearchDropdown = ({ 
  products, 
  value, 
  onChange, 
  onMultiSelectTrigger,
  error 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownListRef = useRef(null);

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width
      });
    }
  }, [isOpen]);

  // Handle window resize and scroll to update position
  useEffect(() => {
    if (!isOpen) return;
    
    const handleUpdatePosition = () => {
      if (inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width
        });
      }
    };
    
    window.addEventListener('resize', handleUpdatePosition);
    window.addEventListener('scroll', handleUpdatePosition);
    
    return () => {
      window.removeEventListener('resize', handleUpdatePosition);
      window.removeEventListener('scroll', handleUpdatePosition);
    };
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && dropdownListRef.current && highlightedIndex >= 0) {
      const items = dropdownListRef.current.querySelectorAll('.dropdown-item');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Filter products based on search term
  const filteredProducts = useMemo(() => {
    if (!searchTerm) return products;
    const term = searchTerm.toLowerCase();
    return products.filter(product => 
      product.ProductCode.toLowerCase().includes(term) || 
      product.ProductName.toLowerCase().includes(term)
    );
  }, [products, searchTerm]);

  // Handle keyboard navigation
   const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        setHighlightedIndex(0);
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev < filteredProducts.length - 1) {
            return prev + 1;
          } else if (filteredProducts.length > 0) {
            return 0; // Loop back to first item
          }
          return prev;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev > 0) {
            return prev - 1;
          } else if (filteredProducts.length > 0) {
            return filteredProducts.length - 1; // Loop to last item
          }
          return prev;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredProducts.length) {
          handleSelectProduct(filteredProducts[highlightedIndex]);
          // Keep dropdown open and focus on search input
          setTimeout(() => {
            if (inputRef.current) {
              inputRef.current.focus();
            }
          }, 0);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      case 's': // Changed from 'p' to 's'
        if (e.ctrlKey) {
          e.preventDefault();
          setIsOpen(false);
          onMultiSelectTrigger();
        }
        break;
      default:
        break;
    }
  };

  const handleSelectProduct = (product) => {
    console.log('Product selected:', product);
    console.log('Product AllowProductTracking:', product.AllowProductTracking);
    onChange(product.ProductCode);
    // Don't close the dropdown
    setSearchTerm('');
    setHighlightedIndex(-1);
    // Keep focus on search input
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Get selected product name
  const selectedProduct = products.find(p => p.ProductCode === value);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className={`w-40 border rounded-lg px-2 py-1 cursor-pointer flex justify-between items-center ${
          error ? 'border-red-400' : 'border-[#E2E8F0]'
        } ${isOpen ? 'ring-1 ring-[#BDDDFC]' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        ref={inputRef}
      >
        <span className="truncate">
          {selectedProduct ? selectedProduct.ProductName : 'Select Product'}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      
      {isOpen && (
        <div 
          className="fixed z-[9999] bg-white border border-[#E2E8F0] rounded-lg shadow-lg"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
          }}
        >
          <div className="p-2 border-b border-[#E2E8F0]">
            <input
              type="text"
              placeholder="Search by ID or name..."
              className="w-full px-2 py-1 border border-[#E2E8F0] rounded focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          
          <div 
            className="max-h-60 overflow-y-auto" 
            ref={dropdownListRef}
          >
            {filteredProducts.length > 0 ? (
              filteredProducts.map((product, index) => (
                <div
                  key={product.ProductCode}
                  className={`dropdown-item px-3 py-2 cursor-pointer hover:bg-[#F1F5F9] ${
                    highlightedIndex === index ? 'bg-[#BDDDFC]' : ''
                  } ${value === product.ProductCode ? 'bg-[#E6F0FF]' : ''}`}
                  onClick={() => handleSelectProduct(product)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="font-medium">{product.ProductName}</div>
                  <div className="text-xs text-gray-500">{product.ProductCode}</div>
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500">No products found</div>
            )}
          </div>
          
          <div className="p-2 border-t border-[#E2E8F0] text-xs text-gray-500">
            Press Ctrl+P for multi-select
          </div>
        </div>
      )}
      
      {error && <span className="text-xs text-red-500">*</span>}
    </div>
  );
};

// Custom Warehouse Dropdown Component with Search
const WarehouseSearchDropdown = ({ 
  warehouses, 
  value, 
  onChange, 
  onMultiSelectTrigger,
  error 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownListRef = useRef(null);

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width
      });
    }
  }, [isOpen]);

  // Handle window resize and scroll to update position
  useEffect(() => {
    if (!isOpen) return;
    
    const handleUpdatePosition = () => {
      if (inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width
        });
      }
    };
    
    window.addEventListener('resize', handleUpdatePosition);
    window.addEventListener('scroll', handleUpdatePosition);
    
    return () => {
      window.removeEventListener('resize', handleUpdatePosition);
      window.removeEventListener('scroll', handleUpdatePosition);
    };
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && dropdownListRef.current && highlightedIndex >= 0) {
      const items = dropdownListRef.current.querySelectorAll('.dropdown-item');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Filter warehouses based on search term
  const filteredWarehouses = useMemo(() => {
    if (!searchTerm) return warehouses;
    const term = searchTerm.toLowerCase();
    return warehouses.filter(warehouse => 
      warehouse.LocationCode.toLowerCase().includes(term) || 
      warehouse.LocationName.toLowerCase().includes(term)
    );
  }, [warehouses, searchTerm]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        setHighlightedIndex(0);
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev < filteredWarehouses.length - 1) {
            return prev + 1;
          } else if (filteredWarehouses.length > 0) {
            return 0; // Loop back to first item
          }
          return prev;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev > 0) {
            return prev - 1;
          } else if (filteredWarehouses.length > 0) {
            return filteredWarehouses.length - 1; // Loop to last item
          }
          return prev;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredWarehouses.length) {
          handleSelectWarehouse(filteredWarehouses[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      case 'w':
        if (e.ctrlKey) {
          e.preventDefault();
          setIsOpen(false);
          onMultiSelectTrigger();
        }
        break;
      default:
        break;
    }
  };

  const handleSelectWarehouse = (warehouse) => {
    onChange(warehouse.LocationCode);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Get selected warehouse name
  const selectedWarehouse = warehouses.find(w => w.LocationCode === value);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className={`w-24 border rounded-lg px-2 py-1 cursor-pointer flex justify-between items-center ${
          error ? 'border-red-400' : 'border-[#E2E8F0]'
        } ${isOpen ? 'ring-1 ring-[#BDDDFC]' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        ref={inputRef}
      >
        <span className="truncate">
          {selectedWarehouse ? selectedWarehouse.LocationName : 'Select'}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      
      {isOpen && (
        <div 
          className="fixed z-[9999] bg-white border border-[#E2E8F0] rounded-lg shadow-lg"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
          }}
        >
          <div className="p-2 border-b border-[#E2E8F0]">
            <input
              type="text"
              placeholder="Search by code or name..."
              className="w-full px-2 py-1 border border-[#E2E8F0] rounded focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          
          <div 
            className="max-h-60 overflow-y-auto" 
            ref={dropdownListRef}
          >
            {filteredWarehouses.length > 0 ? (
              filteredWarehouses.map((warehouse, index) => (
                <div
                  key={warehouse.LocationCode}
                  className={`dropdown-item px-3 py-2 cursor-pointer hover:bg-[#F1F5F9] ${
                    highlightedIndex === index ? 'bg-[#BDDDFC]' : ''
                  } ${value === warehouse.LocationCode ? 'bg-[#E6F0FF]' : ''}`}
                  onClick={() => handleSelectWarehouse(warehouse)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="font-medium">{warehouse.LocationName}</div>
                  <div className="text-xs text-gray-500">{warehouse.LocationCode}</div>
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500">No warehouses found</div>
            )}
          </div>
          
          <div className="p-2 border-t border-[#E2E8F0] text-xs text-gray-500">
            Press Ctrl+W for multi-select
          </div>
        </div>
      )}
      
      {error && <span className="text-xs text-red-500">*</span>}
    </div>
  );
};

// Multi-Select Product Modal Component
const ProductMultiSelectModal = ({ 
  open, 
  onClose, 
  products, 
  onConfirm,
  onSaveToTable,
  onAutofill 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isSaving, setIsSaving] = useState(false);
  const listRef = useRef(null);
  const searchInputRef = useRef(null);
  const tableBodyRef = useRef(null);

  // Filter products based on search term
  const filteredProducts = useMemo(() => {
    if (!searchTerm) return products;
    const term = searchTerm.toLowerCase();
    return products.filter(product => {
      // Search in all specified fields
      return (
        (product.ProductCode && product.ProductCode.toLowerCase().includes(term)) || 
        (product.ProductName && product.ProductName.toLowerCase().includes(term)) ||
        (product.Status && product.Status.toLowerCase().includes(term)) ||
        (product.CategoryName && product.CategoryName.toLowerCase().includes(term)) ||
        (product.UOMName && product.UOMName.toLowerCase().includes(term)) ||
        (product.Description && product.Description.toLowerCase().includes(term)) ||
        (product.BranchName && product.BranchName.toLowerCase().includes(term)) ||
        (product.DepartmentName && product.DepartmentName.toLowerCase().includes(term)) ||
        (product.AllowProductNegativeInventory !== undefined && 
         (product.AllowProductNegativeInventory ? 'yes' : 'no').includes(term)) ||
        (product.AllowBatchOnProduct !== undefined && 
         (product.AllowBatchOnProduct ? 'yes' : 'no').includes(term)) ||
        (product.ProductType && product.ProductType.toLowerCase().includes(term)) ||
        (product.ProductionBOMType && product.ProductionBOMType.toLowerCase().includes(term))
      );
    });
  }, [products, searchTerm]);

  // Check if all filtered products are selected
  const allFilteredSelected = useMemo(() => {
    if (filteredProducts.length === 0) return false;
    return filteredProducts.every(product => selectedProducts.includes(product.ProductCode));
  }, [filteredProducts, selectedProducts]);

  // Check if some filtered products are selected (for indeterminate state)
  const someFilteredSelected = useMemo(() => {
    if (filteredProducts.length === 0) return false;
    return filteredProducts.some(product => selectedProducts.includes(product.ProductCode)) && !allFilteredSelected;
  }, [filteredProducts, selectedProducts, allFilteredSelected]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (open && tableBodyRef.current && highlightedIndex >= 0) {
      const items = tableBodyRef.current.querySelectorAll('.product-row');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, open]);

  // Reset and focus when modal opens
  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setSelectedProducts([]);
      setHighlightedIndex(-1);
      
      // Focus search input after a short delay
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  }, [open]);

  

  // Toggle product selection
  const toggleProductSelection = (productCode) => {
    setSelectedProducts(prev => 
      prev.includes(productCode)
        ? prev.filter(code => code !== productCode)
        : [...prev, productCode]
    );
  };

  // Handle select all filtered products
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Add all filtered products to selection
      const allProductCodes = filteredProducts.map(p => p.ProductCode);
      setSelectedProducts(prev => [...new Set([...prev, ...allProductCodes])]);
    } else {
      // Remove all filtered products from selection
      const filteredProductCodes = filteredProducts.map(p => p.ProductCode);
      setSelectedProducts(prev => prev.filter(code => !filteredProductCodes.includes(code)));
    }
  };

  // Handle clear all selection
  const handleClearAll = () => {
    setSelectedProducts([]);
  };

  // Handle confirm selection
  const handleConfirm = () => {
    onConfirm(selectedProducts);
    onClose();
  };

  // Handle save to table
  const handleSaveToTable = async () => {
    if (selectedProducts.length === 0) return;
    
    setIsSaving(true);
    try {
      await onSaveToTable(selectedProducts);
      alert('Items saved to table successfully!');
    } catch (error) {
      console.error('Error saving to table:', error);
      alert('Failed to save items to table');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Handle autofill
const handleAutofill = useCallback(() => {
    if (selectedProducts.length === 0) return;
    
    try {
      const result = onAutofill(selectedProducts);
      
      if (result && result.success) {
        // Success
      } else {
        alert(result?.error || 'Error autofilling table. Please try again.');
      }
    } catch (error) {
      console.error('Error during autofill:', error);
      alert('Error autofilling table. Please try again.');
    } finally {
      onClose();
    }
  }, [selectedProducts, onAutofill, onClose]);

    useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        handleAutofill();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, handleAutofill]);

  // Handle keyboard navigation - Same as StockIn
 const handleKeyDown = (e) => {
  // Handle search input keys
  if (e.target === searchInputRef.current) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(0);
      // Move focus to the table body
      setTimeout(() => {
        if (tableBodyRef.current) {
          tableBodyRef.current.focus();
        }
      }, 0);
      return;
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredProducts.length > 0) {
        // Select the first product
        toggleProductSelection(filteredProducts[0].ProductCode);
        // Keep focus on search input
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
          }
        }, 0);
      }
      return;
    }
    return;
  }

  // Handle table body navigation
  if (e.target === tableBodyRef.current) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev < filteredProducts.length - 1) {
            return prev + 1;
          } else if (filteredProducts.length > 0) {
            return 0; // Loop back to first item
          }
          return prev;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev > 0) {
            return prev - 1;
          } else if (filteredProducts.length > 0) {
            return filteredProducts.length - 1; // Loop to last item
          }
          return prev;
        });
        break;
      case ' ':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredProducts.length) {
          toggleProductSelection(filteredProducts[highlightedIndex].ProductCode);
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredProducts.length) {
          // Regular Enter for selecting highlighted product
          toggleProductSelection(filteredProducts[highlightedIndex].ProductCode);
        }
        break;
      case 's':
        if (e.ctrlKey) {
          e.preventDefault();
          handleAutofill();
        }
        break;
      case 'Escape':
        onClose();
        break;
      default:
        break;
    }
  }
};

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-2xl w-full h-[100vh] flex flex-col border border-gray-200">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-800">Select Multiple Products</h3>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded-full hover:bg-gray-200"
            >
              <CloseIcon size={24} />
            </button>
          </div>
        </div>
        
        <div className="p-5 border-b border-gray-200 bg-white">
          <div className="relative">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by Product Code, Name, Status, Category, UOM, Description, Branch, Department, etc..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base shadow-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <div className="absolute right-3 top-3 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          <div className="mt-3 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-100">
            <div className="font-medium text-blue-800 mb-1">Keyboard Shortcuts:</div>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-200">
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">↑</kbd>
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">↓</kbd>
                <span className="ml-1">Navigate</span>
              </span>
              <span className="inline-flex items-center px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-200">
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">Space</kbd>
                <span className="ml-1">Select</span>
              </span>
              <span className="inline-flex items-center px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-200">
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">Enter</kbd>
                <span className="ml-1">Select</span>
              </span>
              <span className="inline-flex items-center px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-200">
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">Ctrl</kbd>
                <span className="mx-1">+</span>
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">Enter</kbd>
                <span className="ml-1">Autofill</span>
              </span>
            </div>
          </div>
        </div>
        
        <div 
          ref={listRef}
          className="flex-grow overflow-y-auto bg-gray-50 relative"
        >
          {filteredProducts.length > 0 ? (
            <div className="h-full flex flex-col">
              {/* Single Table with Sticky Header */}
              <div className="flex-grow overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  {/* Sticky Header */}
                  <thead className="sticky top-0 z-30 bg-gray-100 shadow-md">
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-12">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                          checked={allFilteredSelected}
                          ref={(el) => {
                            if (el) {
                              el.indeterminate = someFilteredSelected;
                            }
                          }}
                          onChange={handleSelectAll}
                          title={allFilteredSelected ? "Deselect all" : someFilteredSelected ? "Select all filtered" : "Select all filtered"}
                        />
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-32">
                        Product Code
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider min-w-[200px]">
                        Product Name
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-24">
                        Status
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        Category
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-20">
                        UOM
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider min-w-[180px]">
                        Description
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        Branch
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        Department
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-32">
                        Negative Inv.
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-24">
                        Batch
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        Product Type
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        BOM Type
                      </th>
                    </tr>
                  </thead>
                  
                  {/* Scrollable Body */}
                  <tbody 
                    ref={tableBodyRef}
                    className="bg-white divide-y divide-gray-200 focus:outline-none"
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                  >
                    {filteredProducts.map((product, index) => (
                      <tr
                        key={product.ProductCode}
                        className={`product-row hover:bg-blue-50 transition-colors ${
                          highlightedIndex === index ? 'bg-blue-100 ring-2 ring-blue-500' : ''
                        } ${selectedProducts.includes(product.ProductCode) ? 'bg-blue-50' : ''}`}
                        onClick={() => toggleProductSelection(product.ProductCode)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                            checked={selectedProducts.includes(product.ProductCode)}
                            onChange={() => {}}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 w-32">
                          {product.ProductCode}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium min-w-[200px]">
                          {product.ProductName}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm w-24">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            product.Status === 'Active' ? 'bg-green-100 text-green-800' : 
                            product.Status === 'Inactive' ? 'bg-red-100 text-red-800' : 
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {product.Status || 'N/A'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.CategoryName || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-20">
                          {product.UOMName || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate min-w-[180px]" title={product.Description || ''}>
                          {product.Description || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.BranchName || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.DepartmentName || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm w-32">
                          {product.AllowProductNegativeInventory !== undefined ? (
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              product.AllowProductNegativeInventory ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {product.AllowProductNegativeInventory ? 'Yes' : 'No'}
                            </span>
                          ) : 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm w-24">
                          {product.AllowBatchOnProduct !== undefined ? (
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              product.AllowBatchOnProduct ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {product.AllowBatchOnProduct ? 'Yes' : 'No'}
                            </span>
                          ) : 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.ProductType || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.ProductionBOMType || 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full py-16">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">No products found</h3>
              <p className="mt-2 text-sm text-gray-500">
                {products.length === 0 
                  ? 'No products are available in the system.' 
                  : 'No products match your search criteria. Try different keywords.'}
              </p>
            </div>
          )}
        </div>
        
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-700">
              <span className="font-medium">{selectedProducts.length}</span> product{selectedProducts.length !== 1 ? 's' : ''} selected
            </div>
            {selectedProducts.length > 0 && (
              <button
                type="button"
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                onClick={handleClearAll}
              >
                Clear All
              </button>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={handleSaveToTable}
              disabled={selectedProducts.length === 0 || isSaving}
            >
              <SaveIcon className="mr-2 h-4 w-4" />
              {isSaving ? 'Saving...' : 'Save to Table'}
            </button>
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={handleAutofill}
              disabled={selectedProducts.length === 0}
            >
              <AutofillIcon className="mr-2 h-4 w-4" />
              Autofill Table
            </button>
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              onClick={handleConfirm}
              disabled={selectedProducts.length === 0}
            >
              Add Selected
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};



// For SerialSelectionPopup component
const SerialSelectionPopup = ({ 
  open, 
  onClose, 
  serialData, 
  onSelectSerial,
  requiredQuantity,
  selectedSerials = [],
  productCode,
  warehouseCode,
  warehouseFiltering
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [tempSelectedSerials, setTempSelectedSerials] = useState([]);
  const [displayedSerials, setDisplayedSerials] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Initialize selected serials when popup opens
  useEffect(() => {
    if (open && !isInitialized) {
      console.log('Initializing SerialSelectionPopup with selectedSerials:', selectedSerials);
      console.log('Serial data:', serialData);
      
      const serialNumbers = selectedSerials.map(serial => serial.serialNumber);
      setTempSelectedSerials(serialNumbers);
      setIsInitialized(true);
    }
  }, [open, selectedSerials, serialData, isInitialized]);
  
  // Reset initialization state when popup closes
  useEffect(() => {
    if (!open) {
      setIsInitialized(false);
    }
  }, [open]);
  
  // Get all serials
   const allSerials = Array.isArray(serialData) ? serialData : (serialData?.serials || []);
  
  // Add a safety check to ensure we only show serials for the current product
  const filteredSerials = allSerials.filter(serial => 
    serial.ItemCode === productCode || !serial.ItemCode // Include if ItemCode matches or is not present
  );
  // Filter and sort serials when search term or allSerials changes
 useEffect(() => {
    console.log('Filtering serials. Search term:', searchTerm);
    console.log('All serials:', allSerials);
    
    let filtered = [...allSerials];
    
    // Apply search filter if there's a search term
    if (searchTerm && searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(serial => {
        const serialNumber = (serial.SerialNumber || '').toLowerCase();
        const locationName = (serial.LocationName || '').toLowerCase();
        const warehouseCode = (serial.WarehouseCode || '').toLowerCase();
        
        return (
          serialNumber.includes(searchLower) ||
          locationName.includes(searchLower) ||
          warehouseCode.includes(searchLower)
        );
      });
    }
    
    console.log('Filtered serials:', filtered);
    
    // Sort to show selected items first
    filtered.sort((a, b) => {
      const aSelected = tempSelectedSerials.includes(a.SerialNumber);
      const bSelected = tempSelectedSerials.includes(b.SerialNumber);
      
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    });
    
    setDisplayedSerials(filtered);
  }, [allSerials, searchTerm, tempSelectedSerials]);
  
  // Handle serial selection
  const handleSerialSelect = (serialNumber) => {
    if (tempSelectedSerials.includes(serialNumber)) {
      setTempSelectedSerials(tempSelectedSerials.filter(s => s !== serialNumber));
    } else {
      setTempSelectedSerials([...tempSelectedSerials, serialNumber]);
    }
  };

  // Handle save with useCallback for stable reference
  const handleSave = useCallback(() => {
    if (tempSelectedSerials.length !== requiredQuantity) {
      alert(`Please select exactly ${requiredQuantity} serials. Currently selected: ${tempSelectedSerials.length}`);
      return;
    }
    
    const serialObjects = tempSelectedSerials.map(serialNumber => ({
      serialNumber: serialNumber
    }));
    
    onSelectSerial(serialObjects);
  }, [tempSelectedSerials, requiredQuantity, onSelectSerial]);

  // Handle search key down events (Enter to select first)
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (displayedSerials.length > 0) {
        const firstSerial = displayedSerials[0];
        if (!tempSelectedSerials.includes(firstSerial.SerialNumber)) {
          setTempSelectedSerials(prev => [...prev, firstSerial.SerialNumber]);
        }
      }
    }
  };

  // Add global keyboard listener for Ctrl+S
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, handleSave]);

  // Early return after all hooks are defined
  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50" style={{ zIndex: 1010 }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[80vh] flex flex-col">
        <div className="flex-shrink-0 p-6 border-b flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-[#384959]">Select Serials</h2>
            <div className="mt-2 text-sm text-gray-600">
              <span className="font-medium">Product:</span> 
              <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                {productCode || 'N/A'}
              </span>
            </div>
            {warehouseFiltering && (
              <div className="mt-1 text-sm text-gray-600">
                {warehouseFiltering.enabled ? (
                  warehouseFiltering.applied ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Showing serials from: {warehouseFiltering.warehouseCode}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Showing serials from all warehouses
                    </span>
                  )
                ) : (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    Warehouse filtering disabled
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center">
            <span className="text-sm text-gray-600 mr-4">
              Required: {requiredQuantity} | Selected: {tempSelectedSerials.length}
            </span>
            <button className="text-[#A0AEC0] hover:text-[#384959]" onClick={onClose}>
              <CloseIcon size={24} />
            </button>
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="flex-shrink-0 p-4 border-b">
          <div className="relative">
            <input
              type="text"
              placeholder="Search by serial number, location, or warehouse..."
              className="w-full p-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6A89A7]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown} // Added key down handler
            />
            <svg 
              className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 20 20" 
              fill="currentColor"
            >
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="mt-1 text-xs text-gray-500">
            Showing {displayedSerials.length} of {allSerials.length} serials
          </div>
        </div>
        
        <div className="flex-grow overflow-y-auto p-6">
          {displayedSerials.length > 0 ? (
            <div className="border rounded-lg overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Select</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product Code</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Serial Number</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {displayedSerials.map((serial) => (
                    <tr key={`${serial.SerialNumber}-${Math.random()}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={tempSelectedSerials.includes(serial.SerialNumber)}
                          onChange={() => handleSerialSelect(serial.SerialNumber)}
                          className="h-4 w-4 text-[#6A89A7] focus:ring-[#6A89A7] border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {productCode || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {serial.SerialNumber || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <span className="font-medium">{serial.LocationName || 'N/A'}</span>
                          {serial.WarehouseCode && (
                            <span className="ml-2 text-xs text-gray-400">({serial.WarehouseCode})</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-2">
                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">
                {searchTerm ? 'No matching serials found' : 'No serials found'}
              </h3>
              <p className="text-gray-500">
                {searchTerm 
                  ? `There are no serials matching "${searchTerm}".` 
                  : warehouseFiltering?.enabled && warehouseFiltering?.applied
                    ? `There are no serials available for product ${productCode} in warehouse: ${warehouseFiltering.warehouseCode}`
                    : `There are no serials available for product ${productCode}.`}
              </p>
            </div>
          )}
          
        </div>
        
        {/* Footer */}
        <div className="flex-shrink-0 p-6 border-t bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Selected: {tempSelectedSerials.length} of {requiredQuantity} required
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#6A89A7]"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#6A89A7] hover:bg-[#5a7997] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#6A89A7]"
                onClick={handleSave}
              >
                Save Selection
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// For BatchSelectionPopup component
const BatchSelectionPopup = ({ 
  open, 
  onClose, 
  batchData, 
  onSelectBatch,
  productSettings,
  companySettings,
  requiredQuantity,
  selectedBatches = [],
  productCode,
  warehouseCode,
  warehouseFiltering
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [tempSelectedBatches, setTempSelectedBatches] = useState([]);
  const [errors, setErrors] = useState({});
   const [isInitialized, setIsInitialized] = useState(false);
  
  // Initialize selected batches when popup opens
  // Initialize selected batches when popup opens or when selectedBatches change
  useEffect(() => {
    if (open && !isInitialized) {
      console.log('Initializing BatchSelectionPopup with selectedBatches:', selectedBatches);
      console.log('Batch data:', batchData);
      
      // Ensure batchData is properly structured
      const batches = Array.isArray(batchData) ? batchData : (batchData?.batches || []);
      
      // Initialize with selected batches
      const initializedBatches = selectedBatches.map(selectedBatch => {
        // Find matching batch in available batches
        const batchInfo = batches.find(b => b.BatchNumber === selectedBatch.batchNumber);
        
        return {
          batchNumber: selectedBatch.batchNumber,
          quantity: selectedBatch.quantity || 0,
          expiryDate: selectedBatch.expiryDate || '',
          availableQuantity: batchInfo ? parseFloat(batchInfo.TotalQuantity) || 0 : 0
        };
      });
      
      console.log('Initialized batches:', initializedBatches);
      setTempSelectedBatches(initializedBatches);
      setErrors({});
      setIsInitialized(true);
    }
  }, [open, selectedBatches, batchData, isInitialized]);
  
  // Reset initialization state when popup closes
  useEffect(() => {
    if (!open) {
      setIsInitialized(false);
    }
  }, [open]);
  
  // Ensure batchData is properly structured before filtering
 const batches = Array.isArray(batchData) ? batchData : (batchData?.batches || []);
  
  // Add a safety check to ensure we only show batches for the current product
  const filteredBatches = batches.filter(batch => 
    batch.ItemCode === productCode || !batch.ItemCode // Include if ItemCode matches or is not present
  );
  
  // Filter batch data based on search term and quantity > 0
 const filteredBatchData = filteredBatches.filter(batch => {
  // Check if this batch is already selected
  const isSelected = tempSelectedBatches.some(selected => selected.batchNumber === batch.BatchNumber);
  
  // If batch is selected, include it regardless of quantity
  if (isSelected) {
    return true;
  }
  
  // For non-selected batches, filter out batches with zero or negative quantity
  const hasQuantity = parseFloat(batch.TotalQuantity) > 0;
  
  // Apply search filter if search term is provided
  const matchesSearch = searchTerm === '' || 
    (batch.BatchNumber && batch.BatchNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (batch.LocationName && batch.LocationName.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (batch.WarehouseCode && batch.WarehouseCode.toLowerCase().includes(searchTerm.toLowerCase()));
  
  return hasQuantity && matchesSearch;
}).sort((a, b) => {
  // Sort so that selected batches appear first
  const aSelected = tempSelectedBatches.some(selected => selected.batchNumber === a.BatchNumber);
  const bSelected = tempSelectedBatches.some(selected => selected.batchNumber === b.BatchNumber);
  
  if (aSelected && !bSelected) return -1;
  if (!aSelected && bSelected) return 1;
  return 0;
});

 const handleBatchSelect = (batch) => {
  const batchNumber = batch.BatchNumber;
  const existingIndex = tempSelectedBatches.findIndex(b => b.batchNumber === batchNumber);
  
  if (existingIndex >= 0) {
    // Remove batch if already selected
    const newSelected = [...tempSelectedBatches];
    newSelected.splice(existingIndex, 1);
    setTempSelectedBatches(newSelected);
    
    // Clear any error for this batch
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[batchNumber];
      return newErrors;
    });
  } else {
    // Add new batch with quantity 0
    setTempSelectedBatches([
      ...tempSelectedBatches,
      {
        batchNumber: batch.BatchNumber,
        quantity: 0,
        expiryDate: batch.ExpiryDate ? new Date(batch.ExpiryDate).toISOString().split('T')[0] : '',
        availableQuantity: parseFloat(batch.TotalQuantity) || 0
      }
    ]);
  }
};

 const handleQuantityChange = (batchNumber, quantity) => {
  const newSelected = tempSelectedBatches.map(batch => {
    if (batch.batchNumber === batchNumber) {
      const qty = parseFloat(quantity) || 0;
      const availableQty = batch.availableQuantity;
      
      // Validate quantity doesn't exceed available
      if (qty > availableQty) {
        setErrors(prev => ({
          ...prev,
          [batchNumber]: `Quantity cannot exceed available quantity (${availableQty})`
        }));
        return batch;
      } else if (qty <= 0) {
        // NEW: Validate quantity is greater than zero
        setErrors(prev => ({
          ...prev,
          [batchNumber]: "Quantity must be greater than zero"
        }));
        return batch;
      } else {
        // Clear error for this batch
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[batchNumber];
          return newErrors;
        });
        return { ...batch, quantity: qty };
      }
    }
    return batch;
  });
  
  setTempSelectedBatches(newSelected);
};

  // Handle save with useCallback for stable reference
 const handleSave = useCallback(() => {
  // First, check for zero quantities and set errors for those
  const zeroQuantityErrors = {};
  let hasZeroQuantity = false;
  
  tempSelectedBatches.forEach(batch => {
    if (batch.quantity <= 0) {
      zeroQuantityErrors[batch.batchNumber] = "Quantity must be greater than zero";
      hasZeroQuantity = true;
    }
  });
  
  if (hasZeroQuantity) {
    // Merge with existing errors but only for batches that are still selected
    setErrors(prev => {
      // Create a new errors object that only includes errors for selected batches
      const filteredErrors = {};
      tempSelectedBatches.forEach(batch => {
        if (prev[batch.batchNumber]) {
          filteredErrors[batch.batchNumber] = prev[batch.batchNumber];
        }
      });
      // Add the new zero quantity errors
      return { ...filteredErrors, ...zeroQuantityErrors };
    });
    return;
  }

  // Calculate total selected quantity
  const totalSelected = tempSelectedBatches.reduce((sum, batch) => sum + batch.quantity, 0);
  
  // Validate total matches required quantity
  if (Math.abs(totalSelected - requiredQuantity) > 0.001) {
    alert(`Total batch quantity (${totalSelected}) must match item quantity (${requiredQuantity})`);
    return;
  }
  
  // Check for any other errors - but only for selected batches
  const hasOtherErrors = tempSelectedBatches.some(batch => errors[batch.batchNumber]);
  if (hasOtherErrors) {
    alert('Please fix the errors before saving');
    return;
  }
  
  onSelectBatch(tempSelectedBatches);
}, [tempSelectedBatches, requiredQuantity, errors, onSelectBatch]);

  // Handle search key down events (Enter to select first)
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredBatchData.length > 0) {
        const firstBatch = filteredBatchData[0];
        const existingIndex = tempSelectedBatches.findIndex(b => b.batchNumber === firstBatch.BatchNumber);
        
        if (existingIndex < 0) {
          // Add new batch with quantity 0
          setTempSelectedBatches(prev => [
            ...prev,
            {
              batchNumber: firstBatch.BatchNumber,
              quantity: 0,
              expiryDate: firstBatch.ExpiryDate ? new Date(firstBatch.ExpiryDate).toISOString().split('T')[0] : '',
              availableQuantity: parseFloat(firstBatch.TotalQuantity) || 0
            }
          ]);
        }
      }
    }
  };

  // Add global keyboard listener for Ctrl+S
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, handleSave]);

  // Early return after all hooks are defined
  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50" style={{ zIndex: 1010 }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[80vh] flex flex-col">
        <div className="flex-shrink-0 p-6 border-b flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-[#384959]">Select Batches</h2>
            {/* Warehouse filtering info */}
            {warehouseFiltering && (
              <div className="mt-2 text-sm text-gray-600">
                {warehouseFiltering.enabled ? (
                  warehouseFiltering.applied ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Showing batches from: {warehouseFiltering.warehouseCode}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Showing batches from all warehouses
                    </span>
                  )
                ) : (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    Warehouse filtering disabled
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center">
            <span className="text-sm text-gray-600 mr-4">
              Required Quantity: {requiredQuantity}
            </span>
            <button className="text-[#A0AEC0] hover:text-[#384959]" onClick={onClose}>
              <CloseIcon size={24} />
            </button>
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="flex-shrink-0 p-4 border-b">
          <div className="relative">
            <input
              type="text"
              placeholder="Search by batch number..."
              className="w-full p-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6A89A7]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown} // Added key down handler
            />
            <svg 
              className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 20 20" 
              fill="currentColor"
            >
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
        
        <div className="flex-grow overflow-y-auto p-6">
          {filteredBatchData.length > 0 ? (
            <div className="border rounded-lg overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Select</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product Code</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batch Number</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Available Quantity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity to Use</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredBatchData.map((batch) => {
                    const selectedBatch = tempSelectedBatches.find(b => b.batchNumber === batch.BatchNumber);
                    const isSelected = !!selectedBatch;
                    
                    return (
                      <tr key={batch.BatchNumber} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleBatchSelect(batch)}
                            className="h-4 w-4 text-[#6A89A7] focus:ring-[#6A89A7] border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {productCode || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {batch.BatchNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            <span className="font-medium">{batch.LocationName || 'N/A'}</span>
                            {batch.WarehouseCode && (
                              <span className="ml-2 text-xs text-gray-400">({batch.WarehouseCode})</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {batch.ExpiryDate ? new Date(batch.ExpiryDate).toLocaleDateString() : 'N/A'}
                        </td>
                       

<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
  {batch.TotalQuantity}
  {parseFloat(batch.TotalQuantity) <= 0 && (
    <span className="ml-2 text-xs text-red-500 font-medium">
      (Insufficient Stock)
    </span>
  )}
</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
  {isSelected ? (
    <div className="flex items-center">
      <input
        type="number"
        min="0" // Changed from min="1" to allow zero input but show error
        max={batch.TotalQuantity}
        value={selectedBatch.quantity}
        onChange={(e) => handleQuantityChange(batch.BatchNumber, e.target.value)}
        className={`w-20 border rounded px-2 py-1 ${
          errors[batch.BatchNumber] ? 'border-red-500' : 'border-gray-300'
        }`}
      />
      {errors[batch.BatchNumber] && (
        <span className="text-xs text-red-500 ml-2">
          {errors[batch.BatchNumber]}
        </span>
      )}
    </div>
  ) : (
    <span className="text-gray-400">Select batch</span>
  )}
</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-2">
                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">
                {searchTerm ? 'No matching batches found' : 'No batches found'}
              </h3>
              <p className="text-gray-500">
                {searchTerm 
                  ? 'There are no batches matching your search criteria with available quantity.' 
                  : warehouseFiltering?.enabled && warehouseFiltering?.applied
                    ? `There are no batches available for this product in warehouse: ${warehouseFiltering.warehouseCode}`
                    : 'There are no batches available for this product.'}
              </p>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex-shrink-0 p-6 border-t bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Selected: {tempSelectedBatches.length} batches | 
              Total Quantity: {tempSelectedBatches.reduce((sum, batch) => sum + batch.quantity, 0)}
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#6A89A7]"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#6A89A7] hover:bg-[#5a7997] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#6A89A7]"
                onClick={handleSave}
              >
                Save Selection
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StockOutFormModal = ({ open, onClose, onSubmit, initialData, isEdit }) => {
  const [header, setHeader] = useState(initialHeader);
  const [items, setItems] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dropdownData, setDropdownData] = useState({
    users: [],
    locations: [],
    stockInItems: [],
    branches: [],
    nextStockOutNumber: '',
    nextItemNumber: '',
    products: [],
  });
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);

  // Add to existing state variables
  const [showBatchPopup, setShowBatchPopup] = useState(false);
  const [showSerialPopup, setShowSerialPopup] = useState(false);
  const [batchData, setBatchData] = useState({ batches: [], warehouseFiltering: null });
  const [serialData, setSerialData] = useState({ serials: [], warehouseFiltering: null });
  const [selectedBatchIndex, setSelectedBatchIndex] = useState(null);
  const [selectedSerialIndex, setSelectedSerialIndex] = useState(null);
  
  // New state for maximize/minimize functionality
  const [isMaximized, setIsMaximized] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const modalRef = useRef(null);
  
  // Added state for user management
  const [currentUser, setCurrentUser] = useState(null);
  const [isUserValid, setIsUserValid] = useState(false);
  const [authError, setAuthError] = useState(null);
  
  // Department-related state
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [screenConfig, setScreenConfig] = useState(null);
  const [showDepartmentField, setShowDepartmentField] = useState(false);
  const [showDepartmentItemField, setShowDepartmentItemField] = useState(false);
  
  // New state for stock validation
  const [stockValidation, setStockValidation] = useState({});
  const [validatingStock, setValidatingStock] = useState(false);
  
  // Company settings state
  const [companySettings, setCompanySettings] = useState({
    AllowNegativeInventory: false,
    AllowWarehouseNegativeInventory: false
  });
  
  // State for multi-select modals
  const [showProductMultiSelectModal, setShowProductMultiSelectModal] = useState(false);
  const [showWarehouseMultiSelectModal, setShowWarehouseMultiSelectModal] = useState(false);
  
  const now = new Date().toISOString(); 
  
  // Function to toggle maximize state
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
    setIsMinimized(false);
  };
  
  const toggleMinimize = () => {
        setIsMinimized(!isMinimized);
    setIsMaximized(false);
  };
  
  // Function to restore modal to normal size
  const restoreModal = () => {
    setIsMaximized(false);
    setIsMinimized(false);
  };
  
  // Function to fetch current user
  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setIsUserValid(false);
        setAuthError('No token found. Please log in again.');
        return false;
      }
      const userRes = await fetch(`${API_URL}/current-user`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const userData = await userRes.json();
      
      if (userData.user && userData.user.UserID) {
        setCurrentUser(userData.user);
        setIsUserValid(true);
        setAuthError(null);
        console.log('Current user fetched:', userData);
        return true;
      } else {
        console.error('Invalid user data received:', userData);
        setIsUserValid(false);
        setAuthError('User information not available. Please log in again.');
        return false;
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
      setIsUserValid(false);
      setAuthError('Error fetching user information. Please refresh the page.');
      return false;
    }
  };
  
  // Function to fetch screen configuration
  const fetchScreenConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/screens`);
      const data = await response.json();
      console.log('Fetched screen configurations:', data);
      
      const stockOutScreen = data.screens.find(screen => 
        screen.ScreenName.toLowerCase() === 'StockOut'.toLowerCase()
      );
      
      console.log('StockOut screen configuration:', stockOutScreen);
      if (stockOutScreen) {
        setScreenConfig(stockOutScreen);
        const shouldShowDepartment = stockOutScreen.ShowOnHeader === true;
        setShowDepartmentField(shouldShowDepartment);
        
        const shouldShowDepartmentItem = stockOutScreen.ShowOnItem === true;
        setShowDepartmentItemField(shouldShowDepartmentItem);
        
        if (shouldShowDepartment || shouldShowDepartmentItem) {
          fetchDepartments();
        }
      }
    } catch (error) {
      console.error('Error fetching screen configuration:', error);
      setShowDepartmentField(false);
      setShowDepartmentItemField(false);
    }
  };
  
  // Function to get item details by item code
  const getItemDetails = (itemCode) => {
    const stockItem = dropdownData.stockInItems.find(item => item.ItemCode === itemCode);
    return stockItem ? {
      ItemName: stockItem.ItemName,
      UOM: stockItem.UOM,
      Rate: stockItem.Rate
    } : {
      ItemName: '',
      UOM: '',
      Rate: 0
    };
  };
  
  // Function to validate stock for a specific item using the stock/check API
// Function to validate stock for a specific item using the stock/check API
const validateItemStock = async (itemCode, location, quantity, idx, isQuantityModified, originalQuantity, allowProductNegativeInventory) => {
  if (!itemCode || !location) {
    setStockValidation(prev => ({
      ...prev,
      [idx]: { valid: true, message: '' }
    }));
    return;
  }
  
  if (isEdit && !isQuantityModified) {
    setStockValidation(prev => ({
      ...prev,
      [idx]: { valid: true, message: '' }
    }));
    return;
  }
  
  // Skip validation if both company and product allow negative inventory
  if (companySettings.AllowNegativeInventory && allowProductNegativeInventory) {
    setStockValidation(prev => ({
      ...prev,
      [idx]: { 
        valid: true, 
        message: 'Negative inventory allowed. No validation required.' 
      }
    }));
    return;
  }
  
  setValidatingStock(true);
  try {
    const token = localStorage.getItem('authToken');
    
    // Calculate the change in quantity
    const originalQty = parseFloat(originalQuantity) || 0;
    const newQty = parseFloat(quantity) || 0;
    const qtyChange = newQty - originalQty;
    
    console.log('Validating stock:', { 
      itemCode, 
      location, 
      qtyChange,
      originalQty,
      newQty,
      isEdit,
      isQuantityModified,
      allowProductNegativeInventory
    });
    
    // If quantity is decreased or unchanged, no need to validate
    if (qtyChange <= 0) {
      setStockValidation(prev => ({
        ...prev,
        [idx]: { valid: true, message: '' }
      }));
      return;
    }
    
    // Only validate if quantity is increased
    const response = await axios.get(`${API_URL}/stock/check`, {
      params: {
        itemCode,
        locationCode: location,
        asOfDate: new Date().toISOString(),
        quantity: qtyChange // Only validate the increase
      },
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    const { 
      currentStock, 
      allowed, 
      message,
      companySettings: responseCompanySettings,
      productSettings: responseProductSettings
    } = response.data;
    
    console.log('Stock validation response:', response.data);
    
    setStockValidation(prev => ({
      ...prev,
      [idx]: { 
        valid: allowed, 
        message: message, 
        currentStock,
        companySettings: responseCompanySettings,
        productSettings: responseProductSettings
      }
    }));
  } catch (error) {
    console.error('Error validating stock:', error);
    
    let errorMessage = 'Error checking stock availability';
    
    if (error.response) {
      console.error('Error response:', error.response.data);
      
      // Extract detailed error information
      if (error.response.data) {
        if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
        if (error.response.data.error) {
          errorMessage += `: ${error.response.data.error}`;
        }
        if (error.response.data.stack) {
          console.error('Error stack:', error.response.data.stack);
        }
      }
    } else if (error.request) {
      errorMessage = 'No response from server';
    } else {
      errorMessage = error.message;
    }
    
    console.log('Setting error message:', errorMessage);
    
    setStockValidation(prev => ({
      ...prev,
      [idx]: { valid: false, message: errorMessage }
    }));
  } finally {
    setValidatingStock(false);
  }
};
  
  // Function to validate batch quantity
  const validateBatchQuantity = (idx, item) => {
    // If no batch is selected, no validation needed
    if (!item.batchNumber) return { valid: true, message: '', availableQuantity: 0 };
    
    // Skip validation if both company and product allow negative inventory
    if (companySettings.AllowNegativeInventory && item.allowProductNegativeInventory) {
      return { 
        valid: true, 
        message: 'Negative inventory allowed. No validation required.', 
        availableQuantity: 0 
      };
    }
    
    // Find the selected batch by matching batch number and expiry date (converting to YYYY-MM-DD format)
    const selectedBatch = item.availableBatches.find(
      batch => batch.BatchNumber === item.batchNumber && 
              (item.expiryDate ? 
                (batch.ExpiryDate ? new Date(batch.ExpiryDate).toISOString().split('T')[0] === item.expiryDate : false) 
                : !batch.ExpiryDate)
    );

    console.log("Available batches", item.availableBatches);
    console.log("Selected batch", selectedBatch);
    
    if (!selectedBatch) return { valid: true, message: '', availableQuantity: 0 };
    
    const availableQuantity = selectedBatch.TotalQuantity || 0;
    const originalQuantity = parseFloat(item.originalQuantity) || 0;
    const newQuantity = parseFloat(item.quantityMinus) || 0;
    const qtyChange = newQuantity - originalQuantity;

    console.log(availableQuantity, originalQuantity, newQuantity, qtyChange);
    
    // If quantity is decreased or unchanged, no need to validate
    if (qtyChange <= 0) {
      return { 
        valid: true, 
        message: `Quantity change is valid. Available: ${availableQuantity}`, 
        availableQuantity 
      };
    }
    
    // Only validate if quantity is increased
    if (qtyChange > availableQuantity) {
      return { 
        valid: false, 
        message: `Insufficient stock for batch ${item.batchNumber}. Available: ${availableQuantity}, Requested increase: ${qtyChange}`, 
        availableQuantity 
      };
    }
    
    return { 
      valid: true, 
      message: `Available: ${availableQuantity}`, 
      availableQuantity 
    };
  };

 const fetchProductSettings = async (productCode) => {
  try {
    const response = await fetch(`${API_URL}/products/${productCode}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    const data = await response.json();
    
    // Determine tracking type based on AllowProductTracking value
    let trackingType = 'none';
    const trackingValue = data.AllowProductTracking;
    console.log('Fetched product settings:', data);
    
    // Handle different possible values (number, string, etc.)
    if (trackingValue === 1 || trackingValue === '1' || trackingValue === 'batch') {
      trackingType = 'batch';
    } else if (trackingValue === 2 || trackingValue === '2' || trackingValue === 'serial') {
      trackingType = 'serial';
    }
    
    return {
      allowBatchOnProduct: data.AllowBatchOnProduct,
      allowProductNegativeInventory: data.AllowProductNegativeInventory,
      trackingType: trackingType
    };
  } catch (error) {
    console.error('Error fetching product settings:', error);
    return {
      allowBatchOnProduct: false,
      allowProductNegativeInventory: false,
      trackingType: 'none'
    };
  }
};
const handleBatchClick = async (index, productCode, warehouseCode) => {
  const item = items[index];
  
  console.log('handleBatchClick called:', { index, productCode, warehouseCode, trackingType: item.trackingType });
  
  if (item.trackingType !== 'batch') {
    console.error('Product does not support batch tracking');
    return;
  }
  
  setSelectedBatchIndex(index);
  
  // Fetch fresh batch data without pre-setting existing batches
  await fetchBatchData(productCode, warehouseCode);
  setShowBatchPopup(true);
};

const handleSerialClick = async (index, productCode, warehouseCode) => {
  const item = items[index];
  
  console.log('handleSerialClick called:', { index, productCode, warehouseCode, trackingType: item.trackingType });
  
  if (item.trackingType !== 'serial') {
    console.error('Product does not support serial tracking');
    return;
  }
  
  setSelectedSerialIndex(index);
  
  // Fetch fresh serial data without pre-setting existing serials
  await fetchSerialData(productCode, warehouseCode);
  setShowSerialPopup(true);
};

const fetchBatchData = async (productCode, warehouseCode) => {
  try {
    console.log('Fetching batch data:', { productCode, warehouseCode });

    const url = new URL(`${API_URL}/products/${productCode}/batches`, window.location.origin);
    if (warehouseCode) url.searchParams.append('warehouseCode', warehouseCode);

    const response = await fetch(url.toString(), {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
    });

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const data = await response.json();
    console.log('Batch data received:', data);

    setBatchData({
      batches: Array.isArray(data) ? data : (data?.batches || []),
      warehouseFiltering: data?.warehouseFiltering || {
        enabled: true,
        applied: !!warehouseCode,
        warehouseCode: warehouseCode || 'All'
      }
    });

    return data;

  } catch (error) {
    console.error('Error fetching batch data:', error);
    return batchData;
  }
};

const fetchSerialData = async (productCode, warehouseCode) => {
  try {
    console.log('Fetching serial data:', { productCode, warehouseCode });

    const url = new URL(`${API_URL}/products/${productCode}/serials`, window.location.origin);
    if (warehouseCode) url.searchParams.append('warehouseCode', warehouseCode);

    const response = await fetch(url.toString(), {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
    });

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const data = await response.json();
    console.log('Serial data received:', data);

    setSerialData({
      serials: Array.isArray(data) ? data : (data?.serials || []),
      warehouseFiltering: data?.warehouseFiltering || {
        enabled: true,
        applied: !!warehouseCode,
        warehouseCode: warehouseCode || 'All'
      }
    });

    return data;

  } catch (error) {
    console.error('Error fetching serial data:', error);
    return serialData;
  }
};


const handleBatchSelect = (selectedBatches) => {
  if (selectedBatchIndex !== null) {
    const newItems = [...items];
    const item = newItems[selectedBatchIndex];
    
    // Calculate total batch quantity
    const totalBatchQty = selectedBatches.reduce((sum, batch) => sum + (parseFloat(batch.quantity) || 0), 0);
    
    // Ensure tracking type is preserved
    const trackingType = item.trackingType || 'batch';
    
    newItems[selectedBatchIndex] = {
      ...item,
      batches: selectedBatches.map(batch => ({
        batchNumber: batch.batchNumber,
        quantity: batch.quantity,
        expiryDate: batch.expiryDate
      })),
      quantityMinus: totalBatchQty.toString(), // Always update to match total
      isQuantityModified: true, // Mark as modified since we're changing the quantity
      trackingType: trackingType // Ensure tracking type is preserved
    };
    
    console.log('Updated item after batch selection:', newItems[selectedBatchIndex]);
    
    setItems(newItems);
    setShowBatchPopup(false);
  }
};

const handleSerialSelect = (selectedSerialObjects) => {
  if (selectedSerialIndex !== null) {
    const newItems = [...items];
    const item = newItems[selectedSerialIndex];
    
    // Ensure tracking type is preserved
    const trackingType = item.trackingType || 'serial';
    
    newItems[selectedSerialIndex] = {
      ...item,
      serials: selectedSerialObjects.map(serial => ({
        serialNumber: serial.serialNumber
      })),
      quantityMinus: selectedSerialObjects.length.toString(), // Always update to match count
      isQuantityModified: true, // Mark as modified since we're changing the quantity
      trackingType: trackingType // Ensure tracking type is preserved
    };
    
    console.log('Updated item after serial selection:', newItems[selectedSerialIndex]);
    
    setItems(newItems);
    setShowSerialPopup(false);
  }
};
  
  // Function to close batch popup
  const closeBatchPopup = (idx) => {
    const newItems = [...items];
    newItems[idx].showBatchPopup = false;
    setItems(newItems);
  };
  
  // Calculate item value
  const calculateItemValue = (item) => {
    const quantity = parseFloat(item.quantityMinus) || 0;
    const rate = parseFloat(item.rate) || 0;
    const value = quantity * rate;
    return value.toFixed(2);
  };
  
  // Calculate summary values
  const summary = useMemo(() => {
    let totalQuantity = 0;
    let totalValue = 0;
    
    items.forEach(item => {
      const quantity = parseFloat(item.quantityMinus) || 0;
      const value = parseFloat(item.value) || 0;
      totalQuantity += quantity;
      totalValue += value;
    });
    
    return {
      totalQuantity: totalQuantity.toString(),
      totalValue: totalValue.toFixed(2)
    };
  }, [items]);
  
  // Check if form is valid
// Check if form is valid
const isFormValid = useMemo(() => {
  if (items.length === 0) return false;
  
  for (let idx = 0; idx < items.length; idx++) {
    const item = items[idx];
    
    // Check if batch is required but not provided
    if (item.trackingType === 'batch' && (!item.batches || item.batches.length === 0)) {
      return false;
    }
    
    // Check if serial is required but not provided
    if (item.trackingType === 'serial' && (!item.serials || item.serials.length === 0)) {
      return false;
    }
    
    // For products with 'none' tracking type, use stock validation
    if (item.trackingType === 'none') {
      // Only check validity if the quantity has been modified (in edit mode) or if it's create mode
      if (item.isQuantityModified || !isEdit) {
        if (stockValidation[idx] && !stockValidation[idx].valid) {
          return false;
        }
      }
    }
  }
  
  return true;
}, [items, stockValidation, isEdit]);
  
  // Get quantity validation errors
// Get quantity validation errors
const quantityErrors = useMemo(() => {
  const errs = [];
  
  items.forEach((item, idx) => {
    // Only check stock validation for 'none' tracking type
    if (item.trackingType === 'none') {
      if (item.isQuantityModified || !isEdit) {
        const isValid = stockValidation[idx]?.valid ?? true;
        const message = stockValidation[idx]?.message || '';
        
        if (!isValid) {
          errs.push(`Row ${idx + 1}: ${message}`);
        }
      }
    }
  });
  
  return errs;
}, [items, stockValidation, isEdit]);
  
  // Update header when summary changes
  useEffect(() => {
    setHeader(prev => ({
      ...prev,
      totalQuantity: summary.totalQuantity
    }));
  }, [summary]);
  
useEffect(() => {
  if (open) {
    // Reset form state when opening
    setErrors({});
    setStockValidation({});
    
    fetchCurrentUser();
    fetchScreenConfig();
    fetchCompanySettings();
    
    const initializeForm = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          throw new Error('No authentication token found');
        }
        
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentUser = {
          UserID: payload.userId,
          UserName: payload.email
        };
        
        try {
          const response = await axios.get(`${API_URL}/users/${currentUser.UserID}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          currentUser.UserName = response.data.Username || response.data.FullName || response.data.Email;
        } catch (error) {
          console.error('Error fetching user details:', error);
        }
        
        // Wait for dropdown data to be fetched and get the data
        const data = await fetchDropdownData();
        
        if (isEdit && initialData) {
          setHeader({
            stockOutId: initialData.StockOutID || '',
            stockOutDate: initialData.StockOutDate ? new Date(initialData.StockOutDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            stockOutByUserId: initialData.StockOutByUserID?.toString() || '',
            stockOutUserName: initialData.StockOutUserName || '',
            note: initialData.Note || '',
            status: initialData.Status ? '1' : '0',
            branchCode: initialData.BranchCode || '',
            department_id: initialData.DepartmentID?.toString() || '',
            inventoryFlag: initialData.InventoryFlag ? 'yes' : 'no',
            totalQuantity: initialData.TotalQuantity?.toString() || '0'
          });

          console.log('Initial items data:', initialData.Items);
          
          if (initialData.Items && initialData.Items.length > 0) {
            // Create a promise to fetch batch information for all items that require batches
            const batchPromises = initialData.Items.map(async (item) => {
              const product = data.products.find(p => p.ProductCode === item.ItemCode);
              
              // Default values
              let batchQuantity = Math.abs(item.QuantityMinus) || 0;
              let availableBatches = [];
              
              // If the product allows batches and a batch number is provided, fetch batch information
              if (product && product.AllowBatchOnProduct && item.BatchNumber && item.Location) {
                try {
                  const response = await axios.get(`${API_URL}/products/${item.ItemCode}/batches`, {
                    params: { warehouseCode: item.Location },
                    headers: { 'Authorization': `Bearer ${token}` }
                  });
                  
                  availableBatches = response.data;
                  // Find the specific batch that matches the item's batch number and expiry date
                  const matchedBatch = availableBatches.find(batch => 
                    batch.BatchNumber === item.BatchNumber && 
                    (item.ExpiryDate ? 
                      (batch.ExpiryDate ? new Date(batch.ExpiryDate).toISOString().split('T')[0] === new Date(item.ExpiryDate).toISOString().split('T')[0] : false) 
                      : !batch.ExpiryDate)
                  );
                  
                  // If we found a matching batch, use its quantity
                  if (matchedBatch) {
                    batchQuantity = matchedBatch.TotalQuantity || 0;
                  }
                } catch (error) {
                  console.error('Error fetching batch information:', error);
                }
              }
              
              return {
                ...item,
                availableBatches,
                batchQuantity
              };
            });
            
            // Wait for all batch information to be fetched
            const itemsWithBatchInfo = await Promise.all(batchPromises);
            
            // Now fetch tracking data for all items
            const trackingPromises = initialData.Items.map(async (item) => {
              if (item.StockOutItemID) {
                try {
                  const trackingData = await fetchTrackingData(initialData.StockOutID, item.StockOutItemID);
                  return { stockOutItemId: item.StockOutItemID, trackingData };
                } catch (error) {
                  console.error('Error fetching tracking data:', error);
                  return { stockOutItemId: item.StockOutItemID, trackingData: [] };
                }
              }
              return { stockOutItemId: item.StockOutItemID, trackingData: [] };
            });
            
            const trackingResults = await Promise.all(trackingPromises);
            
            // Create a map for easy lookup
            const trackingMap = {};
            trackingResults.forEach(result => {
              trackingMap[result.stockOutItemId] = result.trackingData;
            });

           // In the useEffect for initializing the form in edit mode
const newItems = itemsWithBatchInfo.map(item => {
  // Try to find the product in the fetched data first
  let product = data.products.find(p => p.ProductCode === item.ItemCode);
  
  // If not found, try to get it directly from the API
  if (!product) {
    console.warn(`Product ${item.ItemCode} not found in dropdown data, fetching directly`);
    
    // We'll create a temporary product object with the item's data
    product = {
      ProductCode: item.ItemCode,
      ProductName: item.ItemName,
      AllowProductTracking: item.TrackingType === 'batch' ? 1 : 
                             item.TrackingType === 'serial' ? 2 : 0,
      AllowBatchOnProduct: item.AllowBatchOnProduct || false,
      AllowProductNegativeInventory: item.AllowProductNegativeInventory || false,
      UOMName: item.UOM
    };
    
    console.log('Created temporary product object:', product);
  }
  
  console.log('Mapping item for edit:', item, product);
  
  // Use the saved tracking type if available, otherwise calculate from product
  let trackingType = 'none';
  
  // First, try to use the saved tracking type from the item data
  if (item.TrackingType) {
    trackingType = item.TrackingType;
    console.log('Using saved tracking type:', trackingType, 'for item:', item.ItemCode);
  } 
  // If no saved tracking type, calculate from product settings
  else if (product) {
    const trackingValue = product.AllowProductTracking;
    console.log('No saved tracking type, calculating from product:', product.ProductCode, 'AllowProductTracking:', trackingValue);
    
    if (trackingValue === 1 || trackingValue === '1' || trackingValue === 'batch') {
      trackingType = 'batch';
    } else if (trackingValue === 2 || trackingValue === '2' || trackingValue === 'serial') {
      trackingType = 'serial';
    }
  }
  
  console.log('Final tracking type for item', item.ItemCode, ':', trackingType);

  // Get tracking data for this item
  const trackingData = trackingMap[item.StockOutItemID] || [];
  console.log('Processing tracking data for item:', item.ItemCode);
  console.log('Tracking data:', trackingData);
  
  // Map tracking data to batches or serials
  let batches = [];
  let serials = [];
  
  if (trackingData.length > 0) {
    if (trackingType === 'batch') {
      batches = trackingData.map(tracking => ({
        batchNumber: tracking.TrackingNumber,
        quantity: tracking.Quantity,
        expiryDate: tracking.ExpiryDate ? new Date(tracking.ExpiryDate).toISOString().split('T')[0] : ''
      }));
      console.log('Processed batches:', batches);
    } else if (trackingType === 'serial') {
      serials = trackingData.map(tracking => ({
        serialNumber: tracking.TrackingNumber
      }));
      console.log('Processed serials:', serials);
    }
  }
  
  // If we have batch data from the item itself (not from tracking API), use that as fallback
  if (trackingType === 'batch' && batches.length === 0 && item.Batches && item.Batches.length > 0) {
    batches = item.Batches.map(batch => ({
      batchNumber: batch.BatchNumber,
      quantity: batch.Quantity,
      expiryDate: batch.ExpiryDate ? new Date(batch.ExpiryDate).toISOString().split('T')[0] : ''
    }));
    console.log('Using fallback batches:', batches);
  }
  
  // If we have serial data from the item itself (not from tracking API), use that as fallback
  if (trackingType === 'serial' && serials.length === 0 && item.Serials && item.Serials.length > 0) {
    serials = item.Serials.map(serial => ({
      serialNumber: serial.SerialNumber
    }));
    console.log('Using fallback serials:', serials);
  }
  
  return {
    itemCode: item.ItemCode || '',
    itemName: item.ItemName || '',
    uom: item.UOM || '',
    quantityMinus: Math.abs(item.QuantityMinus)?.toString() || '',
    originalQuantity: Math.abs(item.QuantityMinus)?.toString() || '',
    rate: item.Rate?.toString() || '',
    value: item.Value?.toString() || '0',
    location: item.Location || '',
    reason: item.Reason || '',
    department_id: item.DepartmentID?.toString() || '',
    isManuallyAdded: false,
    isQuantityModified: false,
    batchNumber: item.BatchNumber || '',
    expiryDate: item.ExpiryDate ? new Date(item.ExpiryDate).toISOString().split('T')[0] : '',
    showBatchPopup: false,
    availableBatches: item.availableBatches || [],
    batchError: '',
    productSelected: product || null,
    originalBatchNumber: item.BatchNumber || '',
    originalExpiryDate: item.ExpiryDate ? new Date(item.ExpiryDate).toISOString().split('T')[0] : '',
    isBatchModified: false,
    allowBatchOnProduct: product ? product.AllowBatchOnProduct : false,
    allowProductNegativeInventory: product ? product.AllowProductNegativeInventory : false,
    batchQuantity: item.batchQuantity || 0,
    trackingType: trackingType,
    batches: batches,
    serials: serials,
    stockOutItemId: item.StockOutItemID // Include StockOutItemID for tracking data fetching
  };
});
            
            setItems(newItems);
            console.log('Mapped items for edit:', newItems);
            
            const selectedFromItems = newItems.map(item => {
              const stockItem = data.stockInItems.find(
                si => si.ItemCode === item.itemCode
              );
              return stockItem ? stockItem : null;
            }).filter(Boolean);
            
            setSelectedProducts(selectedFromItems);
          } else {
            setItems([]);
          }
        } else {
          // FIX: For create mode, preserve the stockOutId that was set in fetchDropdownData
          setHeader({
            ...initialHeader,
            stockOutId: header.stockOutId || data.nextStockOutNumber || '', // Use existing stockOutId if it exists, otherwise use nextStockOutNumber
            stockOutByUserId: currentUser.UserID.toString(),
            stockOutUserName: currentUser.UserName || ''
          });
          setItems([]);
        }
        
        setIsSubmitting(false);
      } catch (error) {
        console.error('Error initializing form:', error);
        alert('Error initializing form. Please try again.');
        onClose();
      }
    };
      
    initializeForm();
  } else {
    // Reset state when closing
    setErrors({});
    setStockValidation({});
  }
}, [open, initialData, isEdit, onClose]);
  
const fetchUsers = async () => {
  try {
    const response = await axios.get(`${API_URL}/user`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    return response.data || [];
  } catch (error) {
    console.error("Error fetching users:", error);
    return [];
  }
};

const fetchLocations = async () => {
  try {
    const response = await axios.get(`${API_URL}/locations`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    return response.data || [];
  } catch (error) {
    console.error("Error fetching locations:", error);
    return [];
  }
};

const fetchProducts = async () => {
  try {
    const response = await axios.get(`${API_URL}/products`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    return response.data || [];
  } catch (error) {
    console.error("Error fetching products:", error);
    return [];
  }
};

const fetchStockInItems = async () => {
  try {
    const response = await axios.get(`${API_URL}/stockin-items`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    return response.data || [];
  } catch (error) {
    console.error("Error fetching stock in items:", error);
    return [];
  }
};

const fetchBranches = async () => {
  try {
    const response = await axios.get(`${API_URL}/branches`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    return response.data || [];
  } catch (error) {
    console.error("Error fetching branches:", error);
    return [];
  }
};

const fetchDepartments = async () => {
  try {
    const response = await axios.get(`${API_URL}/departments`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    return response.data || [];
  } catch (error) {
    console.error("Error fetching departments:", error);
    return [];
  }
};

const fetchCompanySettings = async () => {
  try {
    const response = await axios.get(`${API_URL}/company/settings`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    return response.data || { AllowNegativeInventory: false, AllowWarehouseNegativeInventory: false };
  } catch (error) {
    console.error("Error fetching company settings:", error);
    return { AllowNegativeInventory: false, AllowWarehouseNegativeInventory: false };
  }
};

const fetchNextStockOutNumber = async () => {
  try {
    // This endpoint might not exist separately, so we'll handle it specially
    const response = await axios.get(`${API_URL}/stockout/next-number`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    return response.data.nextStockOutNumber || '';
  } catch (error) {
    console.error("Error fetching next stock out number:", error);
    // Fallback: generate a default number
    return 'STKOUT-1';
  }
};

const fetchNextItemNumber = async () => {
  try {
    // This endpoint might not exist separately, so we'll handle it specially
    const response = await axios.get(`${API_URL}/stockout/next-item-number`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    return response.data.nextItemNumber || '';
  } catch (error) {
    console.error("Error fetching next item number:", error);
    // Fallback: generate a default number
    return 'STKOUTITEM-001';
  }
};

const fetchDropdownData = async () => {
  try {
    setLoading(true);
    
    // Fetch all data in parallel
    const [
      users, 
      locations, 
      products, 
      stockInItems, 
      branches, 
      departments,
      companySettings,
      nextStockOutNumber,
      nextItemNumber
    ] = await Promise.all([
      fetchUsers(),
      fetchLocations(),
      fetchProducts(),
      fetchStockInItems(),
      fetchBranches(),
      fetchDepartments(),
      fetchCompanySettings(),
      fetchNextStockOutNumber(),
      fetchNextItemNumber()
    ]);
    
    const data = {
      users,
      locations,
      products,
      stockInItems,
      nextStockOutNumber,
      nextItemNumber,
      branches,
      departments
    };
    
    console.log('Fetched dropdown data:', data);
    
    setDropdownData(data);
    
    // Store company settings
    setCompanySettings(companySettings);
    
    if (!isEdit) {
      setHeader(prev => ({ 
        ...prev, 
        stockOutId: nextStockOutNumber || ''
      }));
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching dropdown data:', error);
    alert('Error loading form data');
    throw error;
  } finally {
    setLoading(false);
  }
};
  
  const handleHeaderChange = (e) => {
    const { name, value } = e.target;
    setHeader({ ...header, [name]: value });
  };
  
const handleItemChange = async (idx, e) => {
  const { name, value } = e.target;
  console.log(`Item change at index ${idx}:`, name, value);
  
  const newItems = [...items];
  newItems[idx][name] = value;
  
  // If quantity is changed, mark it as modified
  if (name === 'quantityMinus') {
    newItems[idx].isQuantityModified = true;
  }
  
  // If product is selected from dropdown   
  if (name === 'productSelected') {
    const selectedProduct = dropdownData.products.find(p => p.ProductCode === value);
    if (selectedProduct) {
      console.log('Selected product:', selectedProduct);
      console.log('AllowProductTracking value:', selectedProduct.AllowProductTracking);
      
      newItems[idx].itemCode = selectedProduct.ProductCode;
      newItems[idx].itemName = selectedProduct.ProductName;
      newItems[idx].uom = selectedProduct.UOMName;
      newItems[idx].rate = '0'; // Default rate, can be updated later
      newItems[idx].productSelected = selectedProduct;
      newItems[idx].allowBatchOnProduct = selectedProduct.AllowBatchOnProduct;
      newItems[idx].allowProductNegativeInventory = selectedProduct.AllowProductNegativeInventory;

      // Fix: Handle both string and number values for AllowProductTracking
      let trackingType = 'none';
      const trackingValue = selectedProduct.AllowProductTracking;
      
      console.log('Tracking value:', trackingValue, 'Type:', typeof trackingValue);
      
      // Handle different possible values (number, string, etc.)
      if (trackingValue === 1 || trackingValue === '1' || trackingValue === 'batch') {
        trackingType = 'batch';
      } else if (trackingValue === 2 || trackingValue === '2' || trackingValue === 'serial') {
        trackingType = 'serial';
      }
      
      console.log('Setting tracking type to:', trackingType);
      
      newItems[idx].trackingType = trackingType;
      
      // Reset tracking fields when product changes
      newItems[idx].batches = [];
      newItems[idx].serials = [];
      
      // Reset batch fields when product changes
      newItems[idx].batchNumber = '';
      newItems[idx].expiryDate = '';
      newItems[idx].availableBatches = [];
      newItems[idx].batchError = '';
      newItems[idx].originalBatchNumber = '';
      newItems[idx].originalExpiryDate = '';
      newItems[idx].isBatchModified = false;
      newItems[idx].batchQuantity = 0;
      
      // Recalculate value
      newItems[idx].value = calculateItemValue(newItems[idx]);
      
      // Force a state update to ensure tracking type is set immediately
      setItems([...newItems]);
      
      // Validate stock if quantity is already entered and item is manually added
      // Only validate if tracking type is 'none' or 0
      if (newItems[idx].quantityMinus && newItems[idx].location && trackingType === 'none') {
        validateItemStock(
          selectedProduct.ProductCode, 
          newItems[idx].location, 
          newItems[idx].quantityMinus, 
          idx, 
          newItems[idx].isQuantityModified, 
          newItems[idx].originalQuantity,
          newItems[idx].allowProductNegativeInventory
        );
      } else {
        // For batch or serial tracking, clear any previous stock validation
        setStockValidation(prev => {
          const newValidation = { ...prev };
          delete newValidation[idx];
          return newValidation;
        });
      }
    }
  }
  
  // If warehouse changes and product allows batch, reset batch fields
  if (name === 'location' && newItems[idx].productSelected) {
    // Reset batch fields when warehouse changes
    if (newItems[idx].productSelected.AllowBatchOnProduct) {
      newItems[idx].batchNumber = '';
      newItems[idx].expiryDate = '';
      newItems[idx].availableBatches = [];
      newItems[idx].batchError = '';
      newItems[idx].batchQuantity = 0;
    }
    
    // Validate stock when location changes - only for 'none' tracking type
    if (newItems[idx].quantityMinus && newItems[idx].itemCode && newItems[idx].trackingType === 'none') {
      validateItemStock(
        newItems[idx].itemCode, 
        newItems[idx].location, 
        newItems[idx].quantityMinus, 
        idx, 
        newItems[idx].isQuantityModified, 
        newItems[idx].originalQuantity,
        newItems[idx].allowProductNegativeInventory
      );
    } else {
      // For batch or serial tracking, clear any previous validation
      setStockValidation(prev => {
        const newValidation = { ...prev };
        delete newValidation[idx];
        return newValidation;
      });
    }
  }
  
  // If item code changes (for non-dropdown cases), auto-fill item details
  if (name === 'itemCode' && !newItems[idx].isManuallyAdded) {
    const itemDetails = getItemDetails(value);
    newItems[idx].itemName = itemDetails.ItemName;
    newItems[idx].uom = itemDetails.UOM;
    newItems[idx].rate = itemDetails.Rate?.toString() || '0';
    
    // Recalculate value
    newItems[idx].value = calculateItemValue(newItems[idx]);
  }
  
  // Calculate value when quantity or rate changes
  if (name === 'rate') {
    newItems[idx].value = calculateItemValue(newItems[idx]);
  }
  
  // If quantity changes and tracking type is 'none', validate stock
  if (name === 'quantityMinus' && newItems[idx].trackingType === 'none' && newItems[idx].location && newItems[idx].itemCode) {
    validateItemStock(
      newItems[idx].itemCode, 
      newItems[idx].location, 
      newItems[idx].quantityMinus, 
      idx, 
      newItems[idx].isQuantityModified, 
      newItems[idx].originalQuantity,
      newItems[idx].allowProductNegativeInventory
    );
  }
  
  setItems(newItems);
  console.log('Items after change:', newItems);
};
  
  const handleProductSelection = (products, isFinalSelection = false) => {
    setSelectedProducts(products);
    
    if (isFinalSelection) {
      if (isEdit) {
        const existingItemCodes = items.map(item => item.itemCode);
        const newProducts = products.filter(product => !existingItemCodes.includes(product.ItemCode));
        
        // Create new items with default values for UOM and Rate
        const newItems = newProducts.map(product => {
          const stockItem = dropdownData.stockInItems.find(item => 
            item.ItemCode === product.ItemCode
          );
          
          const productDetails = dropdownData.products.find(p => p.ProductCode === product.ItemCode);
          
          // Set tracking type based on AllowProductTracking value
          let trackingType = 'none';
          if (productDetails && productDetails.AllowProductTracking === 1) {
            trackingType = 'batch';
          } else if (productDetails && productDetails.AllowProductTracking === 2) {
            trackingType = 'serial';
          }
          
          return {
            itemCode: product.ItemCode,
            itemName: product.ItemName,
            uom: stockItem?.UOM || '',
            quantityMinus: '',
            originalQuantity: '',
            rate: stockItem?.Rate?.toString() || '0',
            value: '0',
            location: '',
            reason: '',
            department_id: showDepartmentItemField ? "" : undefined,
            isManuallyAdded: false,
            isQuantityModified: false,
            batchNumber: '',
            expiryDate: '',
            showBatchPopup: false,
            availableBatches: [],
            batchError: '',
            productSelected: productDetails || null,
            originalBatchNumber: '',
            originalExpiryDate: '',
            isBatchModified: false,
            allowBatchOnProduct: productDetails ? productDetails.AllowBatchOnProduct : false,
            allowProductNegativeInventory: productDetails ? productDetails.AllowProductNegativeInventory : false,
            batchQuantity: 0,
            trackingType: trackingType,
            batches: [],
            serials: []
          };
        });
        
        setItems(prevItems => [...prevItems, ...newItems]);
        console.log('Items after product selection:', newItems);
      } else {
        // Create new items with default values for UOM and Rate
        const newItems = products.map(product => {
          // Try to find the item details in the stockInItems
          const stockItem = dropdownData.stockInItems.find(item => 
            item.ItemCode === product.ItemCode
          );
          
          // Find product details
          const productDetails = dropdownData.products.find(p => p.ProductCode === product.ItemCode);
          
          return {
            itemCode: product.ItemCode,
            itemName: product.ItemName,
            uom: stockItem?.UOM || '',
            quantityMinus: '',
            originalQuantity: '',
            rate: stockItem?.Rate?.toString() || '0',
            value: '0',
            location: '',
            reason: '',
            department_id: showDepartmentItemField ? "" : undefined,
            isManuallyAdded: false,
            isQuantityModified: false,
            batchNumber: '',
            expiryDate: '',
            showBatchPopup: false,
            availableBatches: [],
            batchError: '',
            productSelected: productDetails || null,
            originalBatchNumber: '',
            originalExpiryDate: '',
            isBatchModified: false,
            allowBatchOnProduct: productDetails ? productDetails.AllowBatchOnProduct : false,
            allowProductNegativeInventory: productDetails ? productDetails.AllowProductNegativeInventory : false,
            batchQuantity: 0
          };
        });
        
        setItems(newItems);
        console.log('Items after product selection:', newItems);
      }
      
      setErrors(prev => {
        const newErrors = { ...prev };
        Object.keys(newErrors).forEach(key => {
          if (key.startsWith('quantityMinus_')) {
            delete newErrors[key];
          }
        });
        return newErrors;
      });
    }
  };

// Function to fetch tracking data for a specific stock out item
const fetchTrackingData = async (stockOutId, stockOutItemId) => {
  try {
    const token = localStorage.getItem('authToken');

    // 🚨 ADD THESE LOGS
    console.log("API_URL =", API_URL);
    const fullUrl = `${API_URL}/stockout/${stockOutId}/item/${stockOutItemId}/tracking`;
    console.log("Final tracking URL =", fullUrl);

    const response = await axios.get(fullUrl, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    console.log('Tracking data for item:', stockOutItemId, response.data);
    return response.data;

  } catch (error) {
    console.error('Error fetching tracking data:', error);
    return [];
  }
};


const autofillSelectedProducts = (selectedProductCodes) => {
  if (selectedProductCodes.length === 0) return { success: false, error: "No products selected" };
  
  try {
    const newItems = selectedProductCodes.map(productCode => {
      const product = dropdownData.products.find(p => p.ProductCode === productCode);
      if (!product) return { ...initialItem };
      
      console.log('Autofill product:', product);
      console.log('AllowProductTracking value:', product.AllowProductTracking);
      
      // Set tracking type based on AllowProductTracking value
      let trackingType = 'none';
      const trackingValue = product.AllowProductTracking;
      
      console.log('Tracking value:', trackingValue, 'Type:', typeof trackingValue);
      
      // Handle different possible values (number, string, etc.)
      if (trackingValue === 1 || trackingValue === '1' || trackingValue === 'batch') {
        trackingType = 'batch';
      } else if (trackingValue === 2 || trackingValue === '2' || trackingValue === 'serial') {
        trackingType = 'serial';
      }
      
      console.log('Setting tracking type to:', trackingType);
      
      return {
        ...initialItem,
        itemCode: product.ProductCode,
        itemName: product.ProductName,
        uom: product.UOMName || product.UOMID || '',
        allowBatchOnProduct: product.AllowBatchOnProduct || false,
        allowProductNegativeInventory: product.AllowProductNegativeInventory || false,
        department_id: showDepartmentItemField ? "" : undefined,
        trackingType: trackingType
      };
    });
    
    // Append new items to existing items instead of replacing them
    setItems(prevItems => [...prevItems, ...newItems]);
    
    // Return success for the modal to show notification
    return { success: true, count: selectedProductCodes.length };
  } catch (error) {
    console.error('Error in autofillSelectedProducts:', error);
    // Return error for the modal to show error message
    return { success: false, error: error.message };
  }
};
  
  // In addItem function
const addItem = () => {
  setItems([...items, { 
    ...initialItem, 
    department_id: showDepartmentItemField ? "" : undefined,
    isManuallyAdded: true,
    productSelected: null,
    isQuantityModified: false,
    originalQuantity: '',
    batchNumber: '',
    expiryDate: '',
    showBatchPopup: false,
    availableBatches: [],
    batchError: '',
    originalBatchNumber: '',
    originalExpiryDate: '',
    isBatchModified: false,
    allowBatchOnProduct: false,
    allowProductNegativeInventory: false,
    batchQuantity: 0,
    trackingType: 'none' // Ensure this is set
  }]);
};

  const removeItem = (idx) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== idx));
    } else if (items.length === 1) {
      setItems([]);
    }
  };
  
 const validate = () => {
  const errs = {};
  if (!header.stockOutId || !header.stockOutId.trim()) errs.stockOutId = 'Required';
  if (!header.stockOutDate) errs.stockOutDate = 'Required';
  if (!header.stockOutByUserId) errs.stockOutByUserId = 'Required';
  if (!header.stockOutUserName) errs.stockOutUserName = 'Required';
  
  if (showDepartmentField && !header.department_id) errs.department_id = 'Required';
  
  if (items.length === 0) {
    errs.items = 'At least one item is required';
  } else {
    items.forEach((item, idx) => {
      if (!item.itemCode) errs[`itemCode_${idx}`] = 'Required';
      if (!item.itemName) errs[`itemName_${idx}`] = 'Required';
      if (!item.uom) errs[`uom_${idx}`] = 'Required';
      if (!item.quantityMinus) errs[`quantityMinus_${idx}`] = 'Required';
      if (!item.rate) errs[`rate_${idx}`] = 'Required';
      if (!item.location) errs[`location_${idx}`] = 'Required';
      
      if (showDepartmentItemField && !item.department_id) errs[`department_id_${idx}`] = 'Required';
      
      if (item.trackingType === 'batch' && (!item.batches || item.batches.length === 0)) {
        errs[`batches_${idx}`] = 'At least one batch is required';
      }
      
      if (item.trackingType === 'serial' && (!item.serials || item.serials.length === 0)) {
        errs[`serials_${idx}`] = 'At least one serial is required';
      }
      
      // Validate quantity matches tracking data
      const quantity = parseInt(item.quantityMinus) || 0;
    
      if (item.trackingType === 'batch' && item.batches && item.batches.length > 0) {
        const totalBatchQty = item.batches.reduce((sum, batch) => sum + (parseInt(batch.quantity) || 0), 0);
        // Use tolerance for floating-point comparison
        if (Math.abs(totalBatchQty - quantity) > 0.001) {
          errs[`quantity_${idx}`] = `Quantity (${quantity}) must match total batch quantity (${totalBatchQty})`;
        }
      }
      
      if (item.trackingType === 'serial' && item.serials && item.serials.length > 0) {
        if (quantity !== item.serials.length) {
          errs[`quantity_${idx}`] = `Quantity (${quantity}) must match number of serials (${item.serials.length})`;
        }
      }
    });
  }
  
  setErrors(errs);
  return Object.keys(errs).length === 0;
};
  
const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validate() || isSubmitting) return;
  
  if (!isUserValid || !currentUser || !currentUser.UserID) {
    alert(authError || 'User information not available. Please refresh the page and log in again.');
    return;
  }
  
  if (quantityErrors.length > 0) {
    alert('Please fix the quantity errors before submitting.');
    return;
  }
  
  setIsSubmitting(true);
  setLoading(true);
  
  try {
    const statusValue = header.status === '1' || header.status === 'Active';
    
    const payload = {
      StockOutID: header.stockOutId,
      StockOutByUserID: parseInt(header.stockOutByUserId),
      StockOutUserName: header.stockOutUserName,
      StockOutDate: header.stockOutDate,
      Note: header.note,
      Status: statusValue,
      BranchCode: header.branchCode,
      InventoryFlag: header.inventoryFlag === 'yes',
      TotalQuantity: parseInt(summary.totalQuantity),
      ...(showDepartmentField && header.department_id ? { department_id: parseInt(header.department_id) } : {}),
      ...(isEdit ? { updated_by: parseInt(currentUser.UserID) } : { created_by: parseInt(currentUser.UserID) }),
      Items: items.map(item => ({
        ItemCode: item.itemCode,
        ItemName: item.itemName,
        UOM: item.uom,
        QuantityMinus: parseInt(item.quantityMinus) || 0,
        Rate: parseFloat(item.rate) || 0,
        Value: parseFloat(item.value) || 0,
        Location: item.location,
        Reason: item.reason,
        ...(showDepartmentItemField && item.department_id ? { department_id: parseInt(item.department_id) } : {}),
        TrackingType: item.trackingType || 'none',
        // Include StockOutItemID if it exists (for edit mode)
        ...(item.stockOutItemId ? { StockOutItemID: item.stockOutItemId } : {}),
        // Fix: Map batches to match server expected format
        Batches: item.batches ? item.batches.map(batch => ({
          BatchNumber: batch.batchNumber,
          Quantity: batch.quantity, // Capital Q to match server expectation
          ExpiryDate: batch.expiryDate
        })) : [],
        // Fix: Map serials to match server expected format
        Serials: item.serials ? item.serials.map(serial => ({
          SerialNumber: serial.serialNumber // Capital S to match server expectation
        })) : []
      }))
    };
    
    const url = isEdit 
      ? `${API_URL}/stockout/${header.stockOutId}`
      : `${API_URL}/stockout`;
    
    const method = isEdit ? 'PUT' : 'POST';
    
    // Fix: Use body instead of data for fetch API
    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}` // Use authToken not token
      },
      body: JSON.stringify(payload)
    });
    
    // Fix: Handle fetch response properly
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Error saving stock out record');
    }
    
    const data = await response.json();
    console.log('Response data:', data);
    
    if (onSubmit) {
      onSubmit();
    }
    
    onClose();
  } catch (err) {
    console.error('Error saving stock out record:', err);
    alert(err.message || 'Error saving stock out record');
  } finally {
    setIsSubmitting(false);
    setLoading(false);
  }
};

  // Handle multi-select products
  const handleMultiSelectProducts = (selectedProductCodes) => {
    if (selectedProductCodes.length === 0) return;
    
    const newItems = selectedProductCodes.map(productCode => {
      const product = dropdownData.products.find(p => p.ProductCode === productCode);
      if (!product) return { ...initialItem };
      
      return {
        ...initialItem,
        itemCode: product.ProductCode,
        itemName: product.ProductName,
        uom: product.UOMName || product.UOMID || '',
        allowBatchOnProduct: product.AllowBatchOnProduct || false,
        allowProductNegativeInventory: product.AllowProductNegativeInventory || false,
        department_id: showDepartmentItemField ? "" : undefined,
      };
    });
    
    // Append new items to existing items instead of replacing them
    setItems(prevItems => [...prevItems, ...newItems]);
  };
  
  // Handle multi-select warehouses
  const handleMultiSelectWarehouses = (selectedWarehouseCodes) => {
    if (selectedWarehouseCodes.length === 0) return;
    
    // For now, just use the first selected warehouse
    // You can modify this logic based on your requirements
    const firstWarehouseCode = selectedWarehouseCodes[0];
    
    // Update all items to use the selected warehouse
    setItems(prevItems => 
      prevItems.map(item => ({
        ...item,
        location: firstWarehouseCode
      }))
    );
  };
  
  // Handle keyboard shortcuts
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      // Check for Alt+P - Multi-select products
      if (e.altKey && e.key === 'p') {
        e.preventDefault();
        setShowProductMultiSelectModal(true);
        return;
      }
      
      
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);
  
  if (!open) return null;
  
  return (
    <div className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .custom-scrollbar::-webkit-scrollbar {
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
          .quantity-exceeded {
            animation: shake 0.5s;
            border-color: #ef4444 !important;
            background-color: #fee2e2 !important;
          }
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            25% { transform: translateX(-5px); }
            75% { transform: translateX(5px); }
          }
          .modal-transition {
            transition: all 0.3s ease-in-out;
          }
        `}
      </style>
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      <div className={`bg-white shadow-2xl w-full flex flex-col transition-transform transform-gpu duration-300 ease-in-out ${
        open ? 'translate-x-0' : 'translate-x-full'
      } ${
        isMaximized ? 'h-full max-w-full' : 'h-full max-w-6xl'
      }`}>
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-8 pb-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-[#384959]">{isEdit ? 'Edit Stock Out Record' : 'Create Stock Out Record'}</h2>
            <div className="flex items-center gap-2">
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" 
                onClick={toggleMaximize}
                title={isMaximized ? "Minimize" : "Maximize"}
              >
                {isMaximized ? <MinimizeIcon size={20} /> : <MaximizeIcon size={20} />}
              </button>
              <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={onClose}>
                <CloseIcon size={24} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Content - only show if not minimized */}
        {!isMinimized && (
          <>
            {/* User validation warning */}
            {open && !isUserValid && (
              <div className="mx-8 mb-4 bg-red-50 border-l-4 border-red-500 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">
                      {authError || 'User information not available. Please refresh the page and log in again.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Scrollable Content Area */}
            <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
              <form onSubmit={handleSubmit} className="space-y-6 pb-6">
                {/* StockOut Header */}
                <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                  <h3 className="text-lg font-semibold text-[#384959] mb-4">Stock Out Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Stock Out ID *</label>
                      <input 
                        name="stockOutId" 
                        value={header.stockOutId} 
                        onChange={handleHeaderChange} 
                        disabled
                        className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]"
                      />
                      {errors.stockOutId && <span className="text-xs text-red-500">{errors.stockOutId}</span>}
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Stock Out Date *</label>
                      <input 
                        type="date" 
                        name="stockOutDate" 
                        value={header.stockOutDate} 
                        onChange={handleHeaderChange} 
                        className={`w-full border rounded-lg px-3 py-2 ${errors.stockOutDate ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]`}
                      />
                      {errors.stockOutDate && <span className="text-xs text-red-500">{errors.stockOutDate}</span>}
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Stock Out By User ID *</label>
                      <input 
                        name="stockOutByUserId" 
                        value={header.stockOutByUserId} 
                        onChange={handleHeaderChange}
                        readOnly
                        className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]"
                        title="Auto-populated from logged-in user"
                      />
                      {errors.stockOutByUserId && <span className="text-xs text-red-500">{errors.stockOutByUserId}</span>}
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Stock Out By User Name *</label>
                      <input 
                        name="stockOutUserName" 
                        value={header.stockOutUserName} 
                        onChange={handleHeaderChange}
                        readOnly
                        className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase"
                        title="Auto-populated from logged-in user"
                      />
                      {errors.stockOutUserName && <span className="text-xs text-red-500">{errors.stockOutUserName}</span>}
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Branch</label>
                      <select 
                        name="branchCode" 
                        value={header.branchCode} 
                        onChange={handleHeaderChange} 
                        className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"
                      >
                        <option value="">Select Branch</option>
                        {dropdownData.branches && dropdownData.branches.map(branch => (
                          <option key={branch.BranchCode} value={branch.BranchCode}>
                            {branch.BranchCode} - {branch.BranchName}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {showDepartmentField && (
                      <div className="flex flex-col gap-2">
                        <label className="block text-sm font-medium text-[#64748b]">
                          Department *
                        </label>
                        <select
                          name="department_id"
                          value={header.department_id}
                          onChange={handleHeaderChange}
                          className={`w-full border rounded-lg px-3 py-2 ${
                            errors.department_id ? "border-red-400" : "border-[#E2E8F0]"
                          } focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase`}
                          disabled={loadingDepartments}
                        >
                          <option value="">{loadingDepartments ? 'Loading departments...' : 'Select Department'}</option>
                          {departments.map((dept) => (
                            <option key={dept.DepartmentID} value={dept.DepartmentID}>
                              {dept.DepartmentCode} - {dept.DepartmentName}
                            </option>
                          ))}
                        </select>
                        {errors.department_id && (
                          <span className="text-xs text-red-500">
                            {errors.department_id}
                          </span>
                        )}
                      </div>
                    )}
                    
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Status</label>
                      <select 
                        name="status" 
                        value={header.status} 
                        onChange={handleHeaderChange} 
                        className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                      >
                        {STATUS_OPTIONS.map(status => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Update Inventory *</label>
                      <select 
                        name="inventoryFlag" 
                        value={header.inventoryFlag} 
                        onChange={handleHeaderChange} 
                        className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                      >
                        {INVENTORY_FLAG_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Select "Yes" to update inventory, "No" to skip inventory update</p>
                    </div>
                    <div className="flex flex-col gap-2 md:col-span-2">
                      <label className="block text-sm font-medium text-[#64748b]">Note</label>
                      <textarea 
                        name="note" 
                        value={header.note} 
                        onChange={handleHeaderChange} 
                        rows={3}
                        className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                      />
                    </div>
                  </div>
                </div>
                
                {/* StockOut Items */}
                <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-[#384959]">Stock Out Items</h3>
                    <div className="flex space-x-2">
                      <button 
                        type="button" 
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center transition-colors" 
                        onClick={addItem}
                      >
                        <PlusIcon size={16} className="mr-1" /> Add New Item
                      </button>
                    </div>
                  </div>
                  
                  {errors.items && (
                    <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg">
                      {errors.items}
                    </div>
                  )}
                  
                  {items.length > 0 ? (
                    <>
                      <div className="overflow-x-auto custom-scrollbar">
                        <table className="min-w-full border border-[#E2E8F0]">
                          <thead>
                            <tr className="bg-[#BDDDFC] text-[#384959]">
                              <th className="p-2">#</th>
                              <th className="p-2">Item Name</th>
                              <th className="p-2">Product Code</th>
                              <th className="p-2">UOM</th>
                              <th className="p-2">Warehouse</th>
                              <th className="p-2">Quantity</th>
                              <th className="p-2">Rate</th>
                              <th className="p-2">Value</th>
                              <th className="p-2">Tracking Type</th>
                              <th className="p-2">Tracking Details</th>
                              {showDepartmentItemField && <th className="p-2">Department</th>}
                              <th className="p-2">Reason</th>
                              <th className="p-2">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item, idx) => (
                              <tr key={idx} className="border-t border-[#E2E8F0]">
                                <td className="p-2 text-center">{idx + 1}</td>
                                <td className="p-2">
                                  {item.isManuallyAdded ? (
                                    <ProductSearchDropdown
                                      products={dropdownData.products}
                                      value={item.productSelected ? item.productSelected.ProductCode : ''}
                                      onChange={(productCode) => {
                                        const selectedProduct = dropdownData.products.find(p => p.ProductCode === productCode);
                                        if (selectedProduct) {
                                          const newItems = [...items];
                                          newItems[idx] = {
                                            ...newItems[idx],
                                            itemCode: selectedProduct.ProductCode,
                                            itemName: selectedProduct.ProductName,
                                            uom: selectedProduct.UOMName || selectedProduct.UOMID || '',
                                            productSelected: selectedProduct,
                                            allowBatchOnProduct: selectedProduct.AllowBatchOnProduct || false,
                                            allowProductNegativeInventory: selectedProduct.AllowProductNegativeInventory || false
                                          };
                                          
                                          // Reset batch fields when product changes
                                          newItems[idx].batchNumber = '';
                                          newItems[idx].expiryDate = '';
                                          newItems[idx].availableBatches = [];
                                          newItems[idx].batchError = '';
                                          newItems[idx].originalBatchNumber = '';
                                          newItems[idx].originalExpiryDate = '';
                                          newItems[idx].isBatchModified = false;
                                          newItems[idx].batchQuantity = 0;
                                          
                                          // Recalculate value
                                          newItems[idx].value = calculateItemValue(newItems[idx]);
                                          
                                          setItems(newItems);
                                        }
                                      }}
                                      onMultiSelectTrigger={() => setShowProductMultiSelectModal(true)}
                                      error={errors[`itemCode_${idx}`]}
                                    />
                                  ) : (
                                    <input 
                                      name="itemName" 
                                      value={item.itemName} 
                                      onChange={e => handleItemChange(idx, e)} 
                                      className={`w-24 border rounded-lg px-2 py-1 ${errors[`itemCode_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0] uppercase'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]`}
                                    />
                                  )}
                                  {errors[`itemCode_${idx}`] && <span className="text-xs text-red-500">*</span>}
                                </td>
                                <td className="p-2">
                                  {item.isManuallyAdded ? (
                                    <input 
                                      name="itemCode" 
                                      value={item.itemCode} 
                                      onChange={e => handleItemChange(idx, e)} 
                                      className={`w-40 border rounded-lg px-2 py-1 ${errors[`itemName_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0] uppercase'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]`}
                                    /> 
                                  ) : (
                                    <input 
                                      name="itemCode" 
                                      value={item.itemCode} 
                                      onChange={e => handleItemChange(idx, e)} 
                                      className={`w-24 border rounded-lg px-2 py-1 ${errors[`itemCode_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0] uppercase'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]`}
                                    />
                                  )}
                                  {errors[`itemName_${idx}`] && <span className="text-xs text-red-500">*</span>}
                                </td>
                                <td className="p-2">
                                  <input 
                                    name="uom" 
                                    value={item.uom} 
                                    readOnly 
                                    className="w-16 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9] uppercase"
                                  />
                                  {errors[`uom_${idx}`] && <span className="text-xs text-red-500">*</span>}
                                </td>
                                <td className="p-2">
                                  <WarehouseSearchDropdown
                                    warehouses={dropdownData.locations}
                                    value={item.location}
                                    onChange={(warehouseCode) => {
                                      const newItems = [...items];
                                      newItems[idx].location = warehouseCode;
                                      
                                      // If product allows batch, reset batch fields when warehouse changes
                                      if (newItems[idx].productSelected && newItems[idx].productSelected.AllowBatchOnProduct) {
                                        newItems[idx].batchNumber = '';
                                        newItems[idx].expiryDate = '';
                                        newItems[idx].availableBatches = [];
                                        newItems[idx].batchError = '';
                                        newItems[idx].batchQuantity = 0;
                                      }
                                      
                                      setItems(newItems);
                                    }}
                                    onMultiSelectTrigger={() => setShowWarehouseMultiSelectModal(true)}
                                    error={errors[`location_${idx}`]}
                                  />
                                </td>
                                
                                <td className="p-2">
                                  <div className="flex flex-col">
                                    <input 
                                      name="quantityMinus" 
                                      type="number" 
                                      min="0" 
                                      value={item.quantityMinus} 
                                      onChange={e => handleItemChange(idx, e)} 
                                      className={`w-16 border rounded-lg px-2 py-1 ${
                                        (item.isQuantityModified && stockValidation[idx]?.valid === false) || 
                                        (item.isQuantityModified && quantityErrors.some(err => err.includes(`Row ${idx + 1}`))) ||
                                        (item.batchError && item.allowBatchOnProduct)
                                          ? 'quantity-exceeded border-red-400 bg-red-50' 
                                          : 'border-[#E2E8F0]'
                                      } focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]`}
                                    />
                                    {item.isQuantityModified && item.itemName && item.location && stockValidation[idx]?.valid === false && !item.allowBatchOnProduct && ( 
                                      <span className="text-xs text-red-500 mt-1">
                                        {stockValidation[idx].message}
                                      </span>
                                    )}
                                    {item.batchError && (
                                      <span className="text-xs text-red-500 mt-1">
                                        {item.batchError}
                                      </span>
                                    )}
                                    {isEdit && !item.isQuantityModified && (
                                      <span className="text-xs text-green-600 mt-1">
                                        Original quantity: {item.originalQuantity}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <input 
                                    name="rate" 
                                    type="number" 
                                    min="0" 
                                    step="0.01"
                                    value={item.rate} 
                                    onChange={e => handleItemChange(idx, e)} 
                                    className={`w-20 border rounded-lg px-2 py-1 ${errors[`rate_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]`}
                                  />
                                  {errors[`rate_${idx}`] && <span className="text-xs text-red-500">*</span>}
                                </td>
                                <td className="p-2">
                                  <input 
                                    name="value" 
                                    value={item.value} 
                                    readOnly 
                                    className="w-20 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]"
                                  />
                                </td>

                                <td className="p-2">
                                  <span className="text-sm font-medium">
                                    {item.trackingType === 'batch' ? 'Batch' : 
                                     item.trackingType === 'serial' ? 'Serial' : 'None'}
                                  </span>
                                </td>

                                <td className="p-2">
                                  {item.trackingType === 'batch' ? (
                                    <div>
                                      <button
                                        type="button"
                                        className="text-blue-600 hover:text-blue-800 text-sm"
                                        onClick={() => handleBatchClick(idx, item.itemCode, item.location)}
                                      >
                                        {item.batches && item.batches.length > 0 
                                          ? `${item.batches.length} batch(es)` 
                                          : 'Select Batches'}
                                      </button>
                                      {errors[`batches_${idx}`] && (
                                        <span className="text-xs text-red-500 block">{errors[`batches_${idx}`]}</span>
                                      )}
                                    </div>
                                  ) : item.trackingType === 'serial' ? (
                                    <div>
                                      <button
                                        type="button"
                                        className="text-blue-600 hover:text-blue-800 text-sm"
                                        onClick={() => handleSerialClick(idx, item.itemCode, item.location)}
                                      >
                                        {item.serials && item.serials.length > 0 
                                          ? `${item.serials.length} serial(s)` 
                                          : 'Select Serials'}
                                      </button>
                                      {errors[`serials_${idx}`] && (
                                        <span className="text-xs text-red-500 block">{errors[`serials_${idx}`]}</span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-gray-500">N/A</span>
                                  )}
                                </td>

                                {showDepartmentItemField && (
                                  <td className="p-2">
                                    <select
                                      name="department_id"
                                      value={item.department_id}
                                      onChange={(e) => handleItemChange(idx, e)}
                                      className={`w-24 border rounded-lg px-2 py-1 ${
                                        errors[`department_id_${idx}`]
                                          ? "border-red-400"
                                          : "border-[#E2E8F0]"
                                      } focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] uppercase`}
                                    >
                                      <option value="">Select</option>
                                      {departments.map((dept) => (
                                        <option key={dept.DepartmentID} value={dept.DepartmentID}>
                                          {dept.DepartmentCode}
                                        </option>
                                      ))}
                                    </select>
                                    {errors[`department_id_${idx}`] && (
                                      <span className="text-xs text-red-500">*</span>
                                    )}
                                  </td>
                                )}
                                <td className="p-2">
                                  <input 
                                    name="reason" 
                                    value={item.reason} 
                                    onChange={e => handleItemChange(idx, e)} 
                                    className="w-32 border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] uppercase"
                                  />
                                </td>
                                <td className="p-2 text-center">
                                  <button 
                                    type="button" 
                                    className="text-red-500 hover:text-red-700 transition-colors" 
                                    onClick={() => removeItem(idx)}
                                  >
                                    <TrashIcon />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      {quantityErrors.length > 0 && (
                        <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg">
                          <h4 className="font-medium mb-2">Quantity Errors:</h4>
                          <ul className="list-disc pl-5 space-y-1">
                            {quantityErrors.map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>No items selected. Click "Add New Item" to add items.</p>
                    </div>
                  )}
                  
                  {items.length > 0 && (
                    <div className="mt-6 bg-white p-4 rounded-lg border border-[#E2E8F0]">
                      <h4 className="text-md font-semibold text-[#384959] mb-4">Total Summary</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-500">Total Quantity</span>
                          <span className="text-base font-semibold">{summary.totalQuantity}</span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-500">Total Value</span>
                          <span className="text-base font-semibold text-lg text-blue-600">{summary.totalValue}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </form>
            </div>
            
            {/* Fixed Footer with Buttons */}
            <div className="flex-shrink-0 p-8 pt-0">
              <div className="flex justify-end gap-3">
                <button 
                  type="button" 
                  className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" 
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-6 py-3 rounded-lg text-white font-semibold shadow-lg transition-all duration-300 ease-in-out transform hover:scale-105 bg-[#6A89A7] hover:bg-[#88BDF2] disabled:opacity-50 disabled:cursor-not-allowed" 
                  disabled={loading || isSubmitting || !isFormValid || !isUserValid}
                  onClick={handleSubmit}
                >
                  {loading || isSubmitting ? 'Saving...' : (isEdit ? 'Update Stock Out' : 'Save Stock Out')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      
      <ProductMultiSelectModal
        open={showProductMultiSelectModal}
        onClose={() => setShowProductMultiSelectModal(false)}
        products={dropdownData.products}
        onConfirm={handleMultiSelectProducts}
        onSaveToTable={() => {}} // Empty function for now
        onAutofill={autofillSelectedProducts} // Pass the autofill function
      />
      
     

  <BatchSelectionPopup 
  open={showBatchPopup}
  onClose={() => setShowBatchPopup(false)}
  batchData={batchData}
  onSelectBatch={handleBatchSelect}
  productSettings={items[selectedBatchIndex]?.productSelected || {}}
  companySettings={companySettings}
  requiredQuantity={Math.abs(parseFloat(items[selectedBatchIndex]?.quantityMinus || 0))}
  selectedBatches={items[selectedBatchIndex]?.batches || []}
  productCode={items[selectedBatchIndex]?.itemCode || ''}
  warehouseCode={items[selectedBatchIndex]?.location || ''}
  warehouseFiltering={batchData?.warehouseFiltering || null}
/>

<SerialSelectionPopup 
  open={showSerialPopup}
  onClose={() => setShowSerialPopup(false)}
  serialData={serialData}
  onSelectSerial={handleSerialSelect}
  requiredQuantity={Math.abs(parseFloat(items[selectedSerialIndex]?.quantityMinus || 0))}
  selectedSerials={items[selectedSerialIndex]?.serials || []}
  productCode={items[selectedSerialIndex]?.itemCode || ''}
  warehouseCode={items[selectedSerialIndex]?.location || ''}
  warehouseFiltering={serialData?.warehouseFiltering || null}
/>
    </div>
  );
};

export default StockOutFormModal;